import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { registerPlugin } from '@capacitor/core';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import SignInModal from './SignInModal';
import NewLoadModal from './NewLoadModal';
import SignaturePad from './SignaturePad';
import AcceptLoadModal from './AcceptLoadModal';
import DeliveryModal from './DeliveryModal';
import GateModal from './GateModal';
import { t } from './translations';
import { QRCodeSVG } from 'qrcode.react';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');

const T = {
  bg:'#0a0c0f',surface:'#12161c',card:'#181d25',border:'#232b36',
  accent:'#e8f01a',accentDk:'#b8c014',
  green:'#22c55e',red:'#ef4444',amber:'#f59e0b',
  muted:'#4a5568',text:'#e2e8f0',soft:'#8892a4',
};

const CITIES = {
  Hamburg:{x:32,y:15},Berlin:{x:47,y:19},Warsaw:{x:63,y:17},
  Rotterdam:{x:17,y:27},Paris:{x:14,y:44},Vienna:{x:56,y:40},
  Munich:{x:40,y:47},Milan:{x:37,y:61},Madrid:{x:9,y:70},
  Oslo:{x:30,y:4},Prague:{x:50,y:29},Lyon:{x:21,y:55},
  Brussels:{x:21,y:34},Frankfurt:{x:33,y:33},Zurich:{x:33,y:49},
  Budapest:{x:58,y:47},Amsterdam:{x:20,y:24},Copenhagen:{x:40,y:8},
  Bucharest:{x:72,y:44},Athens:{x:68,y:72},Helsinki:{x:68,y:2},
  Kyiv:{x:82,y:22},Istanbul:{x:80,y:60},Riga:{x:66,y:10},
};

const ROUTES = [
  {id:1,cities:['Hamburg','Paris'],duration:180,startAt:0},
  {id:2,cities:['Warsaw','Munich'],duration:200,startAt:40},
  {id:3,cities:['Rotterdam','Milan'],duration:240,startAt:80},
  {id:4,cities:['Berlin','Madrid'],duration:300,startAt:20},
  {id:5,cities:['Oslo','Frankfurt'],duration:220,startAt:110},
  {id:6,cities:['Prague','Lyon'],duration:190,startAt:60},
  {id:7,cities:['Brussels','Vienna'],duration:210,startAt:150},
  {id:8,cities:['Amsterdam','Budapest'],duration:250,startAt:30},
  {id:9,cities:['Copenhagen','Munich'],duration:230,startAt:90},
  {id:10,cities:['Madrid','Berlin'],duration:320,startAt:170},
  {id:11,cities:['Milan','Hamburg'],duration:260,startAt:50},
  {id:12,cities:['Paris','Warsaw'],duration:195,startAt:130},
  {id:13,cities:['Frankfurt','Amsterdam'],duration:170,startAt:200},
  {id:14,cities:['Vienna','Oslo'],duration:280,startAt:70},
  {id:15,cities:['Zurich','Copenhagen'],duration:215,startAt:160},
  {id:16,cities:['Bucharest','Vienna'],duration:225,startAt:45},
  {id:17,cities:['Warsaw','Kyiv'],duration:240,startAt:100},
  {id:18,cities:['Athens','Budapest'],duration:290,startAt:75},
  {id:19,cities:['Istanbul','Bucharest'],duration:260,startAt:135},
  {id:20,cities:['Helsinki','Riga'],duration:200,startAt:20},
];

const STATIC_LOADS = Array.from({length:40}).map((_,i)=>{
  const fromCities=['Berlin','Vienna','Warsaw','Rotterdam','Hamburg','Prague'];
  const toCities=['Paris','Madrid','Oslo','Munich','Lyon','Milan'];
  const from=fromCities[i%6], to=toCities[i%6];
  const baseDate=new Date(2025,5,14+(i%7));
  const departHour=6+(i%4)*2;
  const hours=[1050,2600,1900,750,1300][i%5];
  const driveHours=Math.round(hours/80);
  const arriveDate=new Date(baseDate);
  arriveDate.setHours(departHour+driveHours);
  const fmt=(d,h)=>{
    const days=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const months=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${String(h%24).padStart(2,'0')}:00`;
  };
  return {
    id:1000+i,
    shipper:["DHL","LKW Walter","Shell","TotalEnergies","DB Schenker"][i%5],
    from,to,
    price:2200+(i*120),
   weight:[18000,22000,24000,20000,15000][i%5],
    type:["Curtainside","Curtainside","Fuel Tanker","Flatbed","Refrigerated"][i%5],
    distance:[1050,2600,1900,750,1300][i%5],
    depart:fmt(baseDate,departHour),
    arrive:fmt(arriveDate,departHour+driveHours),
    insured:i%3!==0,
    loadRef:null,
  };
});

const BADGE={
  'Curtainside':{bg:'rgba(59,130,246,0.15)',color:'#93c5fd',border:'rgba(59,130,246,0.3)'},
  'Fuel Tanker':{bg:'rgba(239,68,68,0.15)',color:'#fc8181',border:'rgba(239,68,68,0.3)'},
  'Refrigerated':{bg:'rgba(34,197,94,0.12)',color:'#86efac',border:'rgba(34,197,94,0.25)'},
  'Flatbed':{bg:'rgba(234,179,8,0.12)',color:'#fde047',border:'rgba(234,179,8,0.25)'},
};

const VEHICLE_TYPES=[
  {k:'curtainside',icon:'🚛',name:'Curtainside',sub:'Up to 24t / 13.6m',adr:false},
  {k:'flatbed',icon:'🏗️',name:'Flatbed',sub:'Heavy & oversized',adr:false},
  {k:'reefer',icon:'❄️',name:'Refrigerated',sub:'Temperature controlled',adr:false},
  {k:'tanker_fuel',icon:'⛽',name:'Fuel Tanker',sub:'ADR certified',adr:true},
  {k:'tanker_chem',icon:'🧪',name:'Chemical Tanker',sub:'ADR certified',adr:true},
  {k:'van_light',icon:'🚐',name:'Light Van',sub:'Under 3.5t',adr:false},
  {k:'van_heavy',icon:'🚚',name:'Heavy Van',sub:'3.5t to 7.5t',adr:false},
  {k:'container',icon:'📦',name:'Container',sub:'20ft / 40ft',adr:false},
];

const CARGO_TYPES=[
  {k:'general',icon:'📦',name:'General Goods',warn:false,label:'Standard'},
  {k:'food',icon:'🥩',name:'Food & Beverage',warn:false,label:'Temp. sensitive'},
  {k:'metal',icon:'🔩',name:'Metal & Steel',warn:false,label:'Heavy'},
  {k:'wood',icon:'🪵',name:'Timber & Wood',warn:false,label:'Bulky'},
  {k:'chemical',icon:'🧪',name:'Chemicals',warn:true,label:'ADR required'},
  {k:'pharma',icon:'💊',name:'Pharmaceuticals',warn:true,label:'Regulated'},
  {k:'hazmat',icon:'☢️',name:'Hazardous Materials',warn:true,label:'ADR required'},
  {k:'fragile',icon:'🫧',name:'Fragile / Glass',warn:false,label:'Extra care'},
  {k:'livestock',icon:'🐄',name:'Livestock',warn:true,label:'Special permit'},
  {k:'machinery',icon:'⚙️',name:'Machinery',warn:false,label:'Oversized'},
  {k:'textile',icon:'👗',name:'Textile & Clothing',warn:false,label:'Standard'},
  {k:'electronics',icon:'💻',name:'Electronics',warn:false,label:'High value'},
];

const CITY_NAMES=Object.keys(CITIES);
const STEP={GUEST:0,LOGGED_IN:1,PAID:2,DOCS_SENT:3,APPROVED:4};
const genRef=()=>`EUL-${new Date().getFullYear()}-${String(Math.floor(Math.random()*9000)+1000)}`;
// ── فاصله بین دو شهر (بر اساس مختصات نقشه x/y) ──
const cityDistance=(cityA,cityB)=>{
  const a=CITIES[cityA],b=CITIES[cityB];
  if(!a||!b) return 999; // اگر شهر ناشناخته بود، فاصله خیلی زیاد فرض کن
  return Math.sqrt(Math.pow(a.x-b.x,2)+Math.pow(a.y-b.y,2));
};

// ── محاسبه‌ی فاصله‌ی واقعی (کیلومتر) بین دو مختصات جغرافیایی با فرمول Haversine ──
// ضریب 1.3 برای تخمین فاصله‌ی جاده‌ای واقعی (که همیشه از فاصله‌ی مستقیم بیشتره)
const ROAD_DISTANCE_FACTOR=1.3;
const haversineDistance=(lat1,lng1,lat2,lng2)=>{
  const R=6371; // شعاع زمین به کیلومتر
  const dLat=(lat2-lat1)*Math.PI/180;
  const dLng=(lng2-lng1)*Math.PI/180;
  const a=Math.sin(dLat/2)*Math.sin(dLat/2)+
    Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*
    Math.sin(dLng/2)*Math.sin(dLng/2);
  const c=2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
  return Math.round(R*c*ROAD_DISTANCE_FACTOR);
};


const CSS=`
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;900&family=Barlow:wght@400;500;600&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
body{background:#0a0c0f;}
.app{width:100%;min-height:100vh;background:#0a0c0f;font-family:'Barlow',sans-serif;color:#e2e8f0;}
.layout{display:flex;max-width:1200px;margin:0 auto;min-height:100vh;}
.mapcol{flex:1;position:sticky;top:0;height:100vh;display:flex;flex-direction:column;}
.listcol{width:420px;min-width:300px;border-left:1px solid #232b36;overflow-y:auto;height:100vh;}
@media(max-width:768px){
  .layout{flex-direction:column;}
  .mapcol{position:relative;height:320px;flex:none;}
  .listcol{width:100%;border-left:none;border-top:1px solid #232b36;height:auto;}
}
.mapwrap{flex:1;background:#0d1117;position:relative;overflow:hidden;}
.mapgrid{position:absolute;inset:0;
  background-image:linear-gradient(rgba(35,43,54,0.35) 1px,transparent 1px),
  linear-gradient(90deg,rgba(35,43,54,0.35) 1px,transparent 1px);
  background-size:40px 40px;}
.clbl{position:absolute;font-size:9px;font-weight:600;letter-spacing:0.5px;
  color:rgba(136,146,164,0.5);text-transform:uppercase;white-space:nowrap;
  transform:translateX(-50%);pointer-events:none;}
.cdot{position:absolute;width:3px;height:3px;border-radius:50%;
  background:rgba(136,146,164,0.4);transform:translate(-50%,-50%);pointer-events:none;}
.tw{position:absolute;cursor:pointer;z-index:5;}
.tdot{position:absolute;border-radius:50%;background:#e8f01a;transform:translate(-50%,-50%);transition:opacity 0.8s;}
.tring{position:absolute;border-radius:50%;border:1px solid #e8f01a;transform:translate(-50%,-50%);pointer-events:none;}
.ttip{position:absolute;bottom:18px;left:50%;transform:translateX(-50%);
  background:#12161c;border:1px solid #232b36;border-radius:8px;
  padding:5px 10px;font-size:10px;white-space:nowrap;z-index:10;font-weight:600;}
.mapbar{display:flex;border-top:1px solid #232b36;}
.mstat{flex:1;padding:10px 14px;text-align:center;border-right:1px solid #232b36;}
.mstat:last-child{border-right:none;}
.msv{font-family:'Barlow Condensed',sans-serif;font-size:18px;font-weight:900;color:#e8f01a;}
.msl{font-size:9px;color:#8892a4;margin-top:2px;text-transform:uppercase;letter-spacing:0.5px;}
.hdr{padding:14px 16px 10px;background:#12161c;border-bottom:1px solid #232b36;position:sticky;top:0;z-index:20;}
.logo{font-family:'Barlow Condensed',sans-serif;font-size:25px;font-weight:900;letter-spacing:2px;color:white;}
.logo span{color:#e8f01a;}
.pill{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;
  font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;border:1px solid;cursor:pointer;}
.pg{background:rgba(34,197,94,0.1);color:#86efac;border-color:rgba(34,197,94,0.25);}
.pa{background:rgba(245,158,11,0.1);color:#fcd34d;border-color:rgba(245,158,11,0.25);}
.pr2{background:rgba(239,68,68,0.1);color:#fc8181;border-color:rgba(239,68,68,0.3);}
.pl{background:rgba(136,146,164,0.1);color:#8892a4;border-color:rgba(136,146,164,0.2);}
.pdot{width:6px;height:6px;border-radius:50%;}
.ll{padding:10px;}
.lc{background:#181d25;border:1px solid #232b36;border-radius:13px;
  padding:14px;margin-bottom:8px;cursor:pointer;
  transition:border-color 0.2s,transform 0.15s;position:relative;overflow:hidden;}
.lc::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;
  background:#e8f01a;opacity:0;transition:opacity 0.2s;}
.lc:hover{border-color:rgba(232,240,26,0.4);transform:translateX(2px);}
.lc:hover::before{opacity:1;}
.lc.mine{border-color:rgba(232,240,26,0.25);background:#1a1f2a;}
.lc.mine::before{background:#e8f01a;opacity:1;}
.tbg{display:inline-flex;align-items:center;padding:2px 7px;border-radius:5px;
  font-size:10px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;border:1px solid;}
.price{font-family:'Barlow Condensed',sans-serif;font-size:19px;font-weight:900;color:#e8f01a;}
.route{font-size:13px;font-weight:600;display:flex;align-items:center;gap:6px;margin:7px 0 5px;}
.arr{color:#e8f01a;}
.meta{font-size:10px;color:#8892a4;margin-bottom:5px;}
.times{display:flex;gap:12px;font-size:10px;margin-top:4px;}
.time-item{display:flex;flex-direction:column;gap:1px;}
.time-lbl{font-size:9px;text-transform:uppercase;letter-spacing:0.5px;color:#4a5568;}
.time-val{color:#e2e8f0;font-weight:600;}
.vt{display:inline-flex;background:rgba(34,197,94,0.08);border:1px solid rgba(34,197,94,0.2);
  border-radius:5px;padding:2px 6px;font-size:10px;color:#86efac;font-weight:600;}
.it{display:inline-flex;background:rgba(59,130,246,0.08);border:1px solid rgba(59,130,246,0.2);
  border-radius:5px;padding:2px 6px;font-size:10px;color:#93c5fd;font-weight:600;}
.at{font-size:10px;color:#f59e0b;background:rgba(245,158,11,0.08);
  border:1px solid rgba(245,158,11,0.2);border-radius:5px;padding:2px 6px;font-weight:600;}
.mine-tag{font-size:10px;color:#e8f01a;background:rgba(232,240,26,0.08);
  border:1px solid rgba(232,240,26,0.2);border-radius:5px;padding:2px 6px;font-weight:700;}
  .res-ribbon{position:absolute;top:14px;right:-32px;transform:rotate(45deg);
  background:linear-gradient(135deg,#ef4444,#b91c1c);color:#fff;
  font-size:10px;font-weight:800;letter-spacing:1px;padding:4px 40px;
  box-shadow:0 2px 8px rgba(0,0,0,0.4);z-index:3;}
.ref-tag{font-size:9px;color:#4a5568;font-family:'Barlow Condensed',sans-serif;letter-spacing:0.5px;}
.ov{position:fixed;inset:0;background:rgba(0,0,0,0.82);z-index:50;
  display:flex;align-items:flex-end;backdrop-filter:blur(4px);}
@media(min-width:769px){.ov{align-items:center;justify-content:center;}}
.sh{background:#12161c;border:1px solid #232b36;
  border-radius:22px 22px 0 0;padding:26px 22px 38px;
  width:100%;max-width:480px;animation:su 0.25s ease;max-height:92vh;overflow-y:auto;}
@media(min-width:769px){.sh{border-radius:22px;}}
@keyframes su{from{transform:translateY(50px);opacity:0;}to{transform:translateY(0);opacity:1;}}
.hdl{width:36px;height:4px;background:#232b36;border-radius:2px;margin:0 auto 20px;}
.ttl{font-family:'Barlow Condensed',sans-serif;font-size:23px;font-weight:900;margin-bottom:5px;color:#93c5fd;}
.sub{font-size:13px;color:#8892a4;margin-bottom:16px;line-height:1.6;}
.btn{width:100%;padding:14px;border:none;border-radius:12px;
  font-family:'Barlow Condensed',sans-serif;font-size:16px;font-weight:700;
  letter-spacing:1px;text-transform:uppercase;cursor:pointer;transition:all 0.15s;margin-bottom:8px;display:block;}
.ba{background:#e8f01a;color:#0a0c0f;}
.ba:hover{background:#b8c014;}
.ba:disabled{background:#4a5568;color:#8892a4;cursor:not-allowed;}
.bo{background:transparent;color:#e2e8f0;border:1px solid #232b36;}
.bo:hover{border-color:#e8f01a;color:#e8f01a;}
.bs{background:rgba(34,197,94,0.1);color:#86efac;border:1px solid rgba(34,197,94,0.3);}
.bdan{background:rgba(239,68,68,0.1);color:#fc8181;border:1px solid rgba(239,68,68,0.3);}
.inp{width:100%;padding:13px 14px;background:#181d25;border:1px solid #232b36;
  border-radius:11px;color:#e2e8f0;font-family:'Barlow',sans-serif;font-size:14px;
  outline:none;margin-bottom:10px;transition:border-color 0.2s;}
.inp:focus{border-color:#e8f01a;}
.inp::placeholder{color:#4a5568;}
.sel{width:100%;padding:13px 14px;background:#181d25;border:1px solid #232b36;
  border-radius:11px;color:#e2e8f0;font-family:'Barlow',sans-serif;font-size:14px;
  outline:none;margin-bottom:10px;transition:border-color 0.2s;appearance:none;}
.sel:focus{border-color:#e8f01a;}
.otp-inp{width:100%;padding:15px;background:#181d25;border:2px solid #e8f01a;
  border-radius:12px;color:#e8f01a;font-family:'Barlow Condensed',sans-serif;
  font-size:28px;font-weight:900;letter-spacing:12px;text-align:center;outline:none;margin-bottom:14px;}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:13px;}
.grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:9px;margin-bottom:13px;}
.opt{border:1px solid #232b36;border-radius:11px;padding:13px 9px;text-align:center;
  cursor:pointer;background:#181d25;transition:all 0.2s;}
.opt:hover,.opt.sel{border-color:#e8f01a;background:rgba(232,240,26,0.04);}
.opt-icon{font-size:20px;margin-bottom:5px;}
.opt-name{font-family:'Barlow Condensed',sans-serif;font-size:12px;font-weight:700;line-height:1.2;}
.opt-sub{font-size:10px;color:#8892a4;margin-top:2px;}
.sec-lbl{font-size:10px;color:#8892a4;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px;font-weight:600;}
.trow{display:flex;align-items:center;justify-content:space-between;
  padding:12px 13px;background:#181d25;border:1px solid #232b36;
  border-radius:11px;margin-bottom:8px;cursor:pointer;transition:all 0.2s;}
.trow:hover,.trow.sel{border-color:rgba(232,240,26,0.4);}
.trow.sel{background:rgba(232,240,26,0.03);}
.chk{width:20px;height:20px;border-radius:6px;border:1.5px solid #232b36;
  display:flex;align-items:center;justify-content:center;font-size:11px;transition:all 0.2s;flex-shrink:0;}
.chk.on{background:#e8f01a;border-color:#e8f01a;color:#0a0c0f;}
.cargo-grid{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-bottom:13px;}
.cargo-opt{border:1px solid #232b36;border-radius:10px;padding:11px;cursor:pointer;
  background:#181d25;transition:all 0.2s;display:flex;align-items:center;gap:9px;}
.cargo-opt:hover,.cargo-opt.sel{border-color:#e8f01a;background:rgba(232,240,26,0.04);}
.pgbar{display:flex;gap:4px;margin-bottom:16px;}
.pbs{flex:1;height:3px;border-radius:2px;background:#232b36;transition:background 0.3s;}
.pbs.dn{background:#22c55e;}
.pbs.ac{background:#e8f01a;}
.divr{display:flex;align-items:center;gap:10px;margin:12px 0;}
.divr::before,.divr::after{content:'';flex:1;height:1px;background:#232b36;}
.divr span{font-size:11px;color:#4a5568;}
.pb{background:#181d25;border:1px solid #232b36;border-radius:12px;padding:13px;margin-bottom:13px;}
.prow{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
.prow:last-child{margin-bottom:0;}
.warn-box{background:rgba(239,68,68,0.07);border:1px solid rgba(239,68,68,0.2);
  border-radius:10px;padding:11px;margin-bottom:11px;font-size:12px;color:#fc8181;}
.info-pending{background:rgba(245,158,11,0.07);border:1px solid rgba(245,158,11,0.2);
  border-radius:10px;padding:11px;margin-bottom:13px;font-size:12px;color:#fcd34d;line-height:1.8;}
.dr{display:flex;align-items:center;justify-content:space-between;
  padding:12px 14px;background:#181d25;border:1px solid #232b36;
  border-radius:11px;margin-bottom:7px;cursor:pointer;transition:all 0.2s;}
.dr:hover{border-color:#e8f01a;}
.dr.done{border-color:rgba(34,197,94,0.35);background:rgba(34,197,94,0.04);}
.ctr{text-align:center;}
.bico{width:60px;height:60px;border-radius:50%;display:flex;align-items:center;
  justify-content:center;font-size:24px;margin:0 auto 14px;}
.success-ico{width:60px;height:60px;border-radius:50%;background:rgba(34,197,94,0.1);
  border:1px solid rgba(34,197,94,0.3);display:flex;align-items:center;
  justify-content:center;font-size:24px;margin:0 auto 14px;}
.type-badge{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;
  border-radius:8px;font-size:11px;font-weight:600;margin-bottom:14px;}
.type-driver{background:rgba(59,130,246,0.1);color:#93c5fd;border:1px solid rgba(59,130,246,0.25);}
.type-carrier{background:rgba(34,197,94,0.1);color:#86efac;border:1px solid rgba(34,197,94,0.25);}
.type-shipper{background:rgba(232,240,26,0.1);color:#e8f01a;border:1px solid rgba(232,240,26,0.25);}
.status-btn{border:none;border-radius:10px;padding:5px 12px;font-size:10px;font-weight:700;
  cursor:pointer;text-transform:uppercase;letter-spacing:0.5px;font-family:'Barlow Condensed',sans-serif;}
.new-load-btn{border-radius:10px;padding:5px 12px;font-size:10px;font-weight:700;
  cursor:pointer;letter-spacing:0.5px;font-family:'Barlow Condensed',sans-serif;
  background:rgba(232,240,26,0.15);color:#e8f01a;border:1px solid rgba(232,240,26,0.3);}
.new-load-btn:hover{background:rgba(232,240,26,0.25);}
.gps-block{width:100%;height:100vh;background:#0a0c0f;display:flex;flex-direction:column;
  align-items:center;justify-content:center;text-align:center;padding:30px;}
.pkg-toggle{display:flex;gap:8px;margin-bottom:14px;}
.pkg-btn{flex:1;padding:12px;border-radius:11px;border:1px solid #232b36;
  background:#181d25;color:#8892a4;font-family:'Barlow Condensed',sans-serif;
  font-size:14px;font-weight:700;cursor:pointer;transition:all 0.2s;text-transform:uppercase;letter-spacing:0.5px;}
.pkg-btn.act{border-color:#e8f01a;background:rgba(232,240,26,0.06);color:#e8f01a;}
.sig-pad{width:100%;height:120px;background:#0d1117;border:2px dashed #232b36;
  border-radius:12px;display:flex;align-items:center;justify-content:center;
  margin-bottom:10px;cursor:pointer;transition:all 0.2s;position:relative;overflow:hidden;}
.sig-pad.signed{border-color:rgba(34,197,94,0.4);background:rgba(34,197,94,0.04);}
.sig-pad:hover{border-color:#e8f01a;}
.photo-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:13px;}
.photo-slot{height:80px;background:#0d1117;border:2px dashed #232b36;border-radius:10px;
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  cursor:pointer;transition:all 0.2s;gap:4px;}
.photo-slot.done{border-color:rgba(34,197,94,0.4);background:rgba(34,197,94,0.04);}
.photo-slot:hover{border-color:#e8f01a;}
.ins-box{background:rgba(232,240,26,0.05);border:1px solid rgba(232,240,26,0.2);
  border-radius:12px;padding:13px;margin-bottom:13px;}
.ins-box-red{background:rgba(239,68,68,0.05);border:1px solid rgba(239,68,68,0.2);
  border-radius:12px;padding:13px;margin-bottom:13px;}
`;

function Overlay({children,onClose}){
  return(
    <div className="ov" onClick={onClose}>
      <div className="sh" onClick={e=>e.stopPropagation()}>
        <div className="hdl"/>
        {children}
      </div>
    </div>
  );
}

function PBar({steps,cur}){
  return(
    <div className="pgbar">
      {Array.from({length:steps}).map((_,i)=>(
        <div key={i} className={'pbs'+(i<cur?' dn':i===cur?' ac':'')}/>
      ))}
    </div>
  );
}


function TrackingMap({trackedLoad,progress}){
  if(!trackedLoad) return null;
  const from=CITIES[trackedLoad.from];
  const to=CITIES[trackedLoad.to];
  if(!from||!to) return null;
  const truckX=from.x+(to.x-from.x)*progress;
  const truckY=from.y+(to.y-from.y)*progress;
  return(
    <div style={{width:'100%',height:'280px',background:'#0d1117',border:'1px solid #232b36',
      borderRadius:12,position:'relative',overflow:'hidden',marginBottom:14}}>
      <div style={{position:'absolute',inset:0,
        backgroundImage:'linear-gradient(rgba(35,43,54,0.4) 1px,transparent 1px),linear-gradient(90deg,rgba(35,43,54,0.4) 1px,transparent 1px)',
        backgroundSize:'30px 30px'}}/>
      <svg style={{position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none'}}>
        <line x1={from.x+'%'} y1={from.y+'%'} x2={to.x+'%'} y2={to.y+'%'}
          stroke="rgba(59,130,246,0.4)" strokeWidth="2" strokeDasharray="4,4"/>
        <line x1={from.x+'%'} y1={from.y+'%'} x2={truckX+'%'} y2={truckY+'%'}
          stroke="#22c55e" strokeWidth="2.5"/>
      </svg>
      {Object.entries(CITIES).map(([name,pos])=>(
        <React.Fragment key={name}>
          <div style={{position:'absolute',left:pos.x+'%',top:pos.y+'%',width:3,height:3,borderRadius:'50%',background:'rgba(136,146,164,0.4)',transform:'translate(-50%,-50%)'}}/>
          <div style={{position:'absolute',left:pos.x+'%',top:(pos.y+2)+'%',fontSize:8,color:'rgba(136,146,164,0.5)',transform:'translateX(-50%)',whiteSpace:'nowrap'}}>{name}</div>
        </React.Fragment>
      ))}
      <div style={{position:'absolute',left:from.x+'%',top:from.y+'%',transform:'translate(-50%,-50%)',zIndex:3}}>
        <div style={{width:14,height:14,borderRadius:'50%',background:'#22c55e',border:'2px solid #0a0c0f',boxShadow:'0 0 8px #22c55e'}}/>
        <div style={{position:'absolute',top:18,left:'50%',transform:'translateX(-50%)',background:'#12161c',border:'1px solid rgba(34,197,94,0.3)',borderRadius:6,padding:'2px 6px',fontSize:9,color:'#86efac',whiteSpace:'nowrap',fontWeight:700}}>مبدا: {trackedLoad.from}</div>
      </div>
      <div style={{position:'absolute',left:to.x+'%',top:to.y+'%',transform:'translate(-50%,-50%)',zIndex:3}}>
        <div style={{width:14,height:14,borderRadius:'50%',background:'#ef4444',border:'2px solid #0a0c0f',boxShadow:'0 0 8px #ef4444'}}/>
        <div style={{position:'absolute',top:18,left:'50%',transform:'translateX(-50%)',background:'#12161c',border:'1px solid rgba(239,68,68,0.3)',borderRadius:6,padding:'2px 6px',fontSize:9,color:'#fc8181',whiteSpace:'nowrap',fontWeight:700}}>مقصد: {trackedLoad.to}</div>
      </div>
      <div style={{position:'absolute',left:truckX+'%',top:truckY+'%',transform:'translate(-50%,-50%)',zIndex:5}}>
        <div style={{width:30,height:30,borderRadius:'50%',border:'2px solid #3b82f6',animation:'trackPulse 1.8s ease-out infinite',position:'absolute',transform:'translate(-50%,-50%)'}}/>
        <div style={{width:14,height:14,borderRadius:'50%',background:'#3b82f6',border:'2px solid #fff',boxShadow:'0 0 12px #3b82f6',transform:'translate(-50%,-50%)',position:'absolute'}}/>
      </div>
      <div style={{position:'absolute',top:8,left:8,background:'rgba(10,12,15,0.9)',border:'1px solid #232b36',borderRadius:8,padding:'5px 10px'}}>
        <div style={{fontSize:8,color:'#8892a4',textTransform:'uppercase',letterSpacing:1}}>Live Tracking</div>
        <div style={{fontFamily:'Barlow Condensed',fontSize:12,fontWeight:700,color:'#3b82f6'}}>🚛 {trackedLoad.loadRef}</div>
      </div>
      <style>{`@keyframes trackPulse{0%{opacity:0.5;width:30px;height:30px;}100%{opacity:0;width:60px;height:60px;}}`}</style>
    </div>
  );
}
function LiveMap({lang}){
  const [tick,setTick]=useState(0);
  const [hov,setHov]=useState(null);
  const [blinkState,setBlinkState]=useState({});
  const tickRef=useRef(0);
  useEffect(()=>{
    const timers=ROUTES.map(r=>{
      const interval=1500+r.id*430+Math.sin(r.id)*300;
      return setInterval(()=>{setBlinkState(prev=>({...prev,[r.id]:!prev[r.id]}));},interval);
    });
    return()=>timers.forEach(clearInterval);
  },[]);
  
  useEffect(()=>{
    const id=setInterval(()=>{tickRef.current+=1;setTick(tickRef.current);},100);
    return()=>clearInterval(id);
  },[]);
  const getTruckPos=(route)=>{
    const totalTicks=route.duration*60;
    const t=((tickRef.current+route.startAt*10)%(totalTicks*2))/totalTicks;
    const progress=t<=1?t:2-t;
    const from=CITIES[route.cities[0]],to=CITIES[route.cities[1]];
    if(!from||!to) return null;
    return{x:from.x+(to.x-from.x)*progress,y:from.y+(to.y-from.y)*progress,
      label:t<=1?route.cities[0]+' to '+route.cities[1]:route.cities[1]+' to '+route.cities[0]};
  };
  return(
    <div className="mapcol">
      <div className="mapwrap">
        <div className="mapgrid"/>
        <svg style={{position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none'}}>
          {ROUTES.map(r=>{const from=CITIES[r.cities[0]],to=CITIES[r.cities[1]];if(!from||!to) return null;return <line key={r.id} x1={from.x+'%'} y1={from.y+'%'} x2={to.x+'%'} y2={to.y+'%'} stroke="rgba(232,240,26,0.05)" strokeWidth="1" strokeDasharray="3,8"/>;})}</svg>
        {Object.entries(CITIES).map(([name,pos])=>(<React.Fragment key={name}><div className="cdot" style={{left:pos.x+'%',top:pos.y+'%'}}/><div className="clbl" style={{left:pos.x+'%',top:(pos.y+2.5)+'%'}}>{name}</div></React.Fragment>))}
        {ROUTES.map(r=>{
          const pos=getTruckPos(r);if(!pos) return null;
          const isVis=blinkState[r.id]!==false;const size=7+(r.id%3);
          const colors=['#e8f01a','#22c55e','#3b82f6','#f59e0b','#a855f7'];
          const dotColor=colors[r.id%5];
          return(<div key={r.id} className="tw" style={{left:pos.x+'%',top:pos.y+'%'}} onMouseEnter={()=>setHov(r.id)} onMouseLeave={()=>setHov(null)}>
            {isVis&&<div className="tring" style={{width:(size*3)+'px',height:(size*3)+'px',animation:'truckPulse '+(1.6+r.id*0.2)+'s ease-out infinite'}}/>}
            <div className="tdot" style={{width:size+'px',height:size+'px',background:dotColor,opacity:isVis?1:0.12,boxShadow:isVis?'0 0 8px '+dotColor:'none'}}/>
            {hov===r.id&&<div className="ttip">🚛 {pos.label}</div>}
          </div>);
        })}
        <div style={{position:'absolute',top:12,left:12,background:'rgba(10,12,15,0.9)',border:'1px solid #232b36',borderRadius:9,padding:'7px 12px'}}>
          <div style={{fontSize:9,color:'#8892a4',textTransform:'uppercase',letterSpacing:1,marginBottom:1}}>{t(lang,'liveNetwork')}</div>
          <div style={{fontFamily:'Barlow Condensed',fontSize:14,fontWeight:700,color:'#e8f01a'}}>{ROUTES.length} {t(lang,'trucksActive')}</div>
        </div>
        <style>{`@keyframes truckPulse{0%{transform:translate(-50%,-50%) scale(1);opacity:0.5;}70%{transform:translate(-50%,-50%) scale(2.2);opacity:0.1;}100%{transform:translate(-50%,-50%) scale(2.2);opacity:0;}}`}</style>
      </div>
      <div className="mapbar">
        {[['247',t(lang,'members')],['2.4K EUR',t(lang,'avgLoad')],['18',t(lang,'countries')]].map(([v,l])=>(<div key={l} className="mstat"><div className="msv">{v}</div><div className="msl">{l}</div></div>))}
      </div>
    </div>
  );
}

function MembershipPaymentContent({userRole, lang, authUser, companyEmail, setUserStep, setModal, close, STEP, T}){
  const stripe=useStripe();
  const elements=useElements();
  const [subStatus,setSubStatus]=useState('idle'); // idle | processing | succeeded | failed
  const [subError,setSubError]=useState('');
  const [cardReady,setCardReady]=useState(false);
  const continueBtnRef=React.useRef(null);
  React.useEffect(()=>{ if(subStatus==='succeeded'&&continueBtnRef.current){ continueBtnRef.current.focus(); } },[subStatus]);

  useEffect(()=>{
    if(userRole==='carrier') return; // carrier از این مسیر پرداخت نمی‌کند
    const interval=setInterval(()=>{
      if(elements&&elements.getElement(CardNumberElement)){
        setCardReady(true);
        clearInterval(interval);
      }
    },300);
    return()=>clearInterval(interval);
  },[elements,userRole]);

  const priceLabel=userRole==='driver'?'49':userRole==='carrier'?'39':'99';

  // ── مسیر Carrier: پرداخت جدا (بر اساس پلاک) دارد، اینجا فقط عبور از مرحله ──
  if(userRole==='carrier'){
    return(
      <>
        <div className="ttl">{t(lang,'membershipFeeTitle')}</div>
        <div className="sub">{t(lang,'membershipFeeSub')}</div>
        <div className="pb">
          <div className="prow"><span style={{fontSize:13,color:T.soft}}>{t(lang,'roleCarrierCo')}</span><span style={{fontWeight:600}}>{lang==='de'?'Pro Fahrzeug/Monat':'Per vehicle/month'}: 39 EUR</span></div>
        </div>
        <div style={{fontSize:12,color:T.soft,marginBottom:14,lineHeight:1.8}}>{lang==='de'?'Die Zahlung für Fahrzeuge erfolgt separat beim Akzeptieren einer Ladung.':'Vehicle payment is handled separately when accepting a load.'}</div>
        <button className="btn ba" onClick={()=>{setUserStep(STEP.PAID);setModal('docs');}}>{t(lang,'continueBtn')}</button>
      </>
    );
  }

  // ── مسیر Driver / Shipper: Subscription واقعی Stripe ──
  const handleSubscribe=async()=>{
    if(!stripe||!elements) return;
    setSubStatus('processing');
    setSubError('');
    try{
      const email=userRole==='driver'?(authUser?.email||''):companyEmail;
      const res=await fetch('https://oqnrxrowiewwrtroegwl.supabase.co/functions/v1/create-membership-subscription',{
        method:'POST',
        headers:{
          'apikey':'sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Authorization':'Bearer sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Content-Type':'application/json',
        },
        body:JSON.stringify({ auth_id:authUser?.id, email, role:userRole }),
      });
      const data=await res.json();
      if(data.error||!data.client_secret){
        setSubError(data.error||(lang==='de'?'Fehler beim Erstellen des Abonnements':'Error creating subscription'));
        setSubStatus('failed');
        return;
      }
      const cardElement=elements.getElement(CardNumberElement);
      if(!cardElement){
        setSubError(lang==='de'?'Karte noch nicht bereit, bitte kurz warten.':'Card not ready yet, please wait.');
        setSubStatus('idle');
        return;
      }
      const {error,paymentIntent}=await stripe.confirmCardPayment(data.client_secret,{
        payment_method:{ card:cardElement },
      });
      if(error){
        setSubError(error.message);
        setSubStatus('failed');
        return;
      }
      if(paymentIntent&&paymentIntent.status==='succeeded'){
        setSubStatus('succeeded');
      }else{
        setSubError(lang==='de'?'Unerwarteter Status':'Unexpected status: '+paymentIntent?.status);
        setSubStatus('failed');
      }
    }catch(err){
      console.error('خطای کلی handleSubscribe:',err);
      setSubError(String(err));
      setSubStatus('failed');
    }
  };

  return(
    <>
      <div className="ttl">{t(lang,'membershipFeeTitle')}</div>
      <div className="sub">{t(lang,'membershipFeeSub')}</div>
      <div className="pb">
        <div className="prow"><span style={{fontSize:13,color:T.soft}}>{userRole==='driver'?t(lang,'roleIndDriver'):t(lang,'roleShipperCo')}</span><span style={{fontWeight:600}}>{priceLabel} {t(lang,'perMonth')}</span></div>
        <div className="prow" style={{paddingTop:10,borderTop:'1px solid #232b36'}}><span style={{fontFamily:'Barlow Condensed',fontSize:15,fontWeight:700}}>{t(lang,'totalLbl')}</span><span style={{fontFamily:'Barlow Condensed',fontSize:21,fontWeight:900,color:T.accent}}>{priceLabel} {t(lang,'perMonth')}</span></div>
      </div>
      <div style={{fontSize:12,color:T.soft,marginBottom:14,lineHeight:1.8,whiteSpace:'pre-line'}}>{t(lang,'featuresText')}</div>
      <form onSubmit={e=>{ e.preventDefault(); if(subStatus==='succeeded'){ setUserStep(STEP.PAID); if(userRole==='driver'){close();}else{setModal('docs');} }else if(!(subStatus==='processing'||!stripe||!cardReady)){ handleSubscribe(); } }}>
      {subStatus!=='succeeded'&&(
        <div style={{background:'#181d25',border:'1px solid #232b36',borderRadius:11,padding:14,marginBottom:14}}>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:11,color:T.soft,marginBottom:5}}>{lang==='de'?'Kartennummer':'Card number'}</div>
            <CardNumberElement options={{style:{base:{fontSize:'15px',color:'#e2e8f0','::placeholder':{color:'#4a5568'}},invalid:{color:'#fc8181'}}}}/>
          </div>
          <div style={{display:'flex',gap:10}}>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:T.soft,marginBottom:5}}>{lang==='de'?'Ablaufdatum':'Expiry'}</div>
              <CardExpiryElement options={{style:{base:{fontSize:'15px',color:'#e2e8f0','::placeholder':{color:'#4a5568'}},invalid:{color:'#fc8181'}}}}/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:T.soft,marginBottom:5}}>CVC</div>
              <CardCvcElement options={{style:{base:{fontSize:'15px',color:'#e2e8f0','::placeholder':{color:'#4a5568'}},invalid:{color:'#fc8181'}}}}/>
            </div>
          </div>
        </div>
      )}
      {subStatus==='failed'&&<div className="warn-box">⚠️ {subError}</div>}
      {subStatus==='succeeded'&&(
        <div style={{background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:10,padding:13,marginBottom:14,fontSize:12,color:'#86efac',textAlign:'center'}}>✓ {lang==='de'?'Zahlung erfolgreich':'Payment successful'}</div>
      )}
      {subStatus!=='succeeded'?(
        <button type="submit" className="btn ba" disabled={subStatus==='processing'||!stripe||!cardReady}>
          {subStatus==='processing'?(lang==='de'?'Wird verarbeitet...':'Processing...'):`${t(lang,'payBtn')} ${priceLabel} EUR`}
        </button>
      ):(
        <button type="submit" className="btn ba" ref={continueBtnRef}>{t(lang,'continueBtn')}</button>
      )}
      </form>
    </>
  );
}

const PayerPaymentPage=({load,token,lang})=>{
  const stripe=useStripe();
  const elements=useElements();
  const [status,setStatus]=useState('idle');
  const [error,setError]=useState('');
  const [cardReady,setCardReady]=useState(false);

  useEffect(()=>{
    const interval=setInterval(()=>{
      if(elements&&elements.getElement(CardNumberElement)){
        setCardReady(true);
        clearInterval(interval);
      }
    },300);
    return()=>clearInterval(interval);
  },[elements]);

  const handlePay=async()=>{
    if(!stripe||!elements) return;
    setStatus('processing');
    setError('');
    try{
      const res=await fetch('https://oqnrxrowiewwrtroegwl.supabase.co/functions/v1/create-payment',{
        method:'POST',
        headers:{
          'apikey':'sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Authorization':'Bearer sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Content-Type':'application/json',
        },
        body:JSON.stringify({ load_ref:load.load_ref, amount:load.price }),
      });
      const data=await res.json();
      if(data.error||!data.client_secret){
        setError(data.error||'Fehler');
        setStatus('failed');
        return;
      }
      const cardElement=elements.getElement(CardNumberElement);
      const {error:stripeErr,paymentIntent}=await stripe.confirmCardPayment(data.client_secret,{
        payment_method:{ card:cardElement },
      });
      if(stripeErr){
        setError(stripeErr.message);
        setStatus('failed');
        return;
      }
      if(paymentIntent&&paymentIntent.status==='requires_capture'){
        const confirmRes=await fetch('https://oqnrxrowiewwrtroegwl.supabase.co/functions/v1/confirm-payer-payment',{
          method:'POST',
          headers:{
            'apikey':'sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
            'Authorization':'Bearer sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
            'Content-Type':'application/json',
          },
          body:JSON.stringify({ payment_token:token, payment_intent_id:paymentIntent.id }),
        });
        const confirmData=await confirmRes.json();
        if(confirmData.error){
          setError(confirmData.error);
          setStatus('failed');
          return;
        }
        setStatus('authorized');
      }else{
        setError('Unerwarteter Status: '+paymentIntent?.status);
        setStatus('failed');
      }
    }catch(err){
      console.error('خطای کلی PayerPaymentPage:',err);
      setError(String(err));
      setStatus('failed');
    }
  };

  if(status==='authorized') return(
    <div style={{maxWidth:420,margin:'60px auto',padding:24,textAlign:'center'}}>
      <div className="success-ico" style={{background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.3)'}}>✓</div>
      <div className="ttl">{lang==='de'?'Zahlung erfolgreich autorisiert':'Payment successfully authorized'}</div>
      <div className="sub">{lang==='de'?'Die Ladung ist jetzt für Fahrer sichtbar. Der Betrag wird erst nach erfolgreicher Lieferung abgebucht.':'The load is now visible to drivers. The amount will only be charged after successful delivery.'}</div>
    </div>
  );

  return(
    <div style={{maxWidth:420,margin:'40px auto',padding:24}}>
      <div className="logo" style={{marginBottom:20}}>EU<span>.</span>LOAD</div>
      <div className="ttl">{lang==='de'?'Zahlungsanfrage':'Payment Request'}</div>
      <div className="sub">{load.from_city} → {load.to_city}</div>
      <div className="pb">
        <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Referenz':'Reference'}</span><span style={{fontSize:12,fontWeight:600}}>{load.load_ref}</span></div>
        <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Gewicht':'Weight'}</span><span style={{fontSize:12,fontWeight:600}}>{load.total_weight} kg</span></div>
        <div className="prow"><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Betrag':'Amount'}</span><span style={{fontSize:16,fontWeight:900,color:'#e8f01a'}}>{load.price} EUR</span></div>
      </div>
      <div style={{background:'#181d25',border:'1px solid #232b36',borderRadius:11,padding:14,marginBottom:10}}>
        <div style={{fontSize:11,color:T.soft,marginBottom:5}}>{lang==='de'?'Kartennummer':'Card number'}</div>
        <CardNumberElement options={{style:{base:{fontSize:'15px',color:'#e2e8f0','::placeholder':{color:'#4a5568'}},invalid:{color:'#fc8181'}}}}/>
      </div>
      <div style={{display:'flex',gap:10,marginBottom:14}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.soft,marginBottom:5}}>{lang==='de'?'Ablaufdatum':'Expiry'}</div>
          <div style={{background:'#181d25',border:'1px solid #232b36',borderRadius:11,padding:14}}>
            <CardExpiryElement options={{style:{base:{fontSize:'15px',color:'#e2e8f0','::placeholder':{color:'#4a5568'}},invalid:{color:'#fc8181'}}}}/>
          </div>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.soft,marginBottom:5}}>CVC</div>
          <div style={{background:'#181d25',border:'1px solid #232b36',borderRadius:11,padding:14}}>
            <CardCvcElement options={{style:{base:{fontSize:'15px',color:'#e2e8f0','::placeholder':{color:'#4a5568'}},invalid:{color:'#fc8181'}}}}/>
          </div>
        </div>
      </div>
      {status==='failed'&&<div className="warn-box">⚠️ {error}</div>}
      <button className="btn ba" disabled={status==='processing'||!stripe||!cardReady} onClick={handlePay}>
        {status==='processing'?(lang==='de'?'Wird verarbeitet...':'Processing...'):(lang==='de'?'Jetzt bezahlen':'Pay now')+' '+load.price+' EUR'}
      </button>
    </div>
  );
};


export default function App(){
  const [userStep,setUserStep]=useState(STEP.GUEST);
  const [userRole,setUserRole]=useState(null);
  const [avail,setAvail]=useState('available');
  const [selLoad,setSelLoad]=useState(null);
  const [modal,setModal]=useState(null);
  const [docs,setDocs]=useState([]);
  const [docFiles,setDocFiles]=useState({});
  const [docUploading,setDocUploading]=useState({});
  const [gpsOk,setGpsOk]=useState(false);
  const [gpsAsked,setGpsAsked]=useState(false);
  const [gpsSkipped,setGpsSkipped]=useState(false);
  const [lang,setLang]=useState('de');
  const [screen,setScreen]=useState('user_type');
  const [isLogin,setIsLogin]=useState(false);
  const [driverPhone,setDriverPhone]=useState('');
  const [driverOtp,setDriverOtp]=useState('');
  const [driverPass,setDriverPass]=useState('');
  const [driverName,setDriverName]=useState('');
  const [customerNumber,setCustomerNumber]=useState('');
  const [driverVehicle,setDriverVehicle]=useState(null);
  const [driverPlate,setDriverPlate]=useState('');
  const [driverCapacity,setDriverCapacity]=useState('');
  const [companyEmail,setCompanyEmail]=useState('');
  const [companyEmailOtp,setCompanyEmailOtp]=useState('');
  const [companyPass,setCompanyPass]=useState('');
  const [companyPass2,setCompanyPass2]=useState('');
  const [companyName,setCompanyName]=useState('');
  const [companyReg,setCompanyReg]=useState('');
  const [companyVat,setCompanyVat]=useState('');
  const [companyActivity,setCompanyActivity]=useState('');
  const [companyAddress,setCompanyAddress]=useState('');
  const [companyPostal,setCompanyPostal]=useState('');
  const [companyPostalResults,setCompanyPostalResults]=useState([]);
  const [companyPostalStatus,setCompanyPostalStatus]=useState('idle');
  const [companySelectedCity,setCompanySelectedCity]=useState(null);
  const [companyStreetAddress,setCompanyStreetAddress]=useState('');
  const [companyGeoStatus,setCompanyGeoStatus]=useState('idle');
  const [companyCoords,setCompanyCoords]=useState(null);
  const [myCompanyLat,setMyCompanyLat]=useState(null);
  const [myCompanyLng,setMyCompanyLng]=useState(null);
  const [companyPhone,setCompanyPhone]=useState('');
  const [companyWebsite,setCompanyWebsite]=useState('');
  const [companyVehicles,setCompanyVehicles]=useState([]);
  const [companyCargo,setCompanyCargo]=useState([]);
  const [companyPlates,setCompanyPlates]=useState([]);
  const [companyDocFile,setCompanyDocFile]=useState(null);
  const [companyDocUploading,setCompanyDocUploading]=useState(false);
  const [loginContact,setLoginContact]=useState('');
  const [loginPass,setLoginPass]=useState('');
  const [myLoads,setMyLoads]=useState([]);
  const [dbLoads,setDbLoads]=useState([]);
  const [authUser,setAuthUser]=useState(null);
  const [showLogin,setShowLogin]=useState(false);
const [loginPhone,setLoginPhone]=useState('');
const [loginPassword,setLoginPassword]=useState('');
const loginPhoneRef=useRef(null);
const loginPasswordRef=useRef(null);
const [authLoading,setAuthLoading]=useState(true);
  const [newLoadStep,setNewLoadStep]=useState(0);
  const [nlFrom,setNlFrom]=useState('');
  const [nlTo,setNlTo]=useState('');
  const [nlFromPostal,setNlFromPostal]=useState('');
  const [nlToPostal,setNlToPostal]=useState('');
  const [nlFromResults,setNlFromResults]=useState([]);
  const [nlToResults,setNlToResults]=useState([]);
  const [nlFromSelected,setNlFromSelected]=useState(null); // {city,state,country,lat,lng}
  const [nlToSelected,setNlToSelected]=useState(null);
  const [fromPostalStatus,setFromPostalStatus]=useState('idle'); // idle|checking|multiple|invalid|confirmed
  const [toPostalStatus,setToPostalStatus]=useState('idle');
  const [nlDate,setNlDate]=useState('');
  const [nlTime,setNlTime]=useState('');
  const [nlCargoType,setNlCargoType]=useState('');
  const [nlPkgType,setNlPkgType]=useState('pallet');
  const [nlPallets,setNlPallets]=useState('');
  const [nlPalletWeight,setNlPalletWeight]=useState('');
  const [nlLength,setNlLength]=useState('');
  const [nlWidth,setNlWidth]=useState('');
  const [nlHeight,setNlHeight]=useState('');
  const [nlWeight,setNlWeight]=useState('');
  const [nlVehicle,setNlVehicle]=useState('');
  const [nlPrice,setNlPrice]=useState('');
  const [nlCargoValue,setNlCargoValue]=useState('');
  const [nlInsured,setNlInsured]=useState(true);
  const [nlInsuranceMode,setNlInsuranceMode]=useState('none'); // 'has' | 'euload' | 'none'
  const [nlPayerType,setNlPayerType]=useState('sender'); // 'sender' | 'receiver' | 'third_party'
  const [nlPayerEmail,setNlPayerEmail]=useState('');
  const [nlPayerName,setNlPayerName]=useState('');
  const [serverDate,setServerDate]=useState(null);
  const [loadShipment,setLoadShipment]=useState(null);
  const [showProfile,setShowProfile]=useState(false);
  const [showMyLoads,setShowMyLoads]=useState(false);
  const [showShipperLoads,setShowShipperLoads]=useState(false);
  const [shipperAllLoads,setShipperAllLoads]=useState([]);
  const [showDeliveryProof,setShowDeliveryProof]=useState(false);
  const [deliveryProofData,setDeliveryProofData]=useState(null);
  const [showDeliveryConfirm,setShowDeliveryConfirm]=useState(false);
  const [deliveryConfirmData,setDeliveryConfirmData]=useState(null);
  const [confirmDamageNote,setConfirmDamageNote]=useState('');
  const [showShipmentDoc,setShowShipmentDoc]=useState(false);
  const [shipmentDocData,setShipmentDocData]=useState(null);
  const [payerToken,setPayerToken]=useState(null);
  const [verifyLoadRef,setVerifyLoadRef]=useState(null);
  const [verifyData,setVerifyData]=useState(null);
  const [verifyLoading,setVerifyLoading]=useState(true);
  const [verifyError,setVerifyError]=useState('');
  const [payerLoad,setPayerLoad]=useState(null);
  const [payerLoadLoading,setPayerLoadLoading]=useState(true);
  const [payerStatus,setPayerStatus]=useState('idle'); // idle | processing | authorized | failed | already_paid
  const [payerError,setPayerError]=useState('');
  const [pendingUsers,setPendingUsers]=useState([]);
  const [adminSelectedUser,setAdminSelectedUser]=useState(null);
  const [adminUserDocUrls,setAdminUserDocUrls]=useState({});
  const [adminLoading,setAdminLoading]=useState(false);
  const [myActiveLoads,setMyActiveLoads]=useState([]);
const [activeSessions,setActiveSessions]=useState([]);
  const [myDeliveredLoads,setMyDeliveredLoads]=useState([]);
  const [newPassword,setNewPassword]=useState('');
  const [newPassword2,setNewPassword2]=useState('');
  const [showNewPass,setShowNewPass]=useState(false);
  const [showNewPass2,setShowNewPass2]=useState(false);
  const [nlNotes,setNlNotes]=useState('');
  const [nlReceiverName,setNlReceiverName]=useState('');
  const [nlPickupAddress,setNlPickupAddress]=useState('');
const [nlDeliveryAddress,setNlDeliveryAddress]=useState('');
const [nlPickupCoords,setNlPickupCoords]=useState(null);
const [nlDeliveryCoords,setNlDeliveryCoords]=useState(null);
const [pickupAddrStatus,setPickupAddrStatus]=useState('idle');
const [deliveryAddrStatus,setDeliveryAddrStatus]=useState('idle');
  const [nlSubmitting,setNlSubmitting]=useState(false);
  const [nlGeneratedRef,setNlGeneratedRef]=useState(null);

  // ── جدید: state های قبول بار و امضا ──
  const [acceptStep,setAcceptStep]=useState(0);
  // بیمه
  const [insHasPolicy,setInsHasPolicy]=useState(null); // null | true | false
  const [insPolicyNum,setInsPolicyNum]=useState('');
  const [insCompany,setInsCompany]=useState('');
  const [insExpiry,setInsExpiry]=useState('');
  const [insEuloadPaid,setInsEuloadPaid]=useState(false);
  // عکس‌ها
  const [photos,setPhotos]=useState([null,null,null,null]);
  // امضاها
  const [sigSender,setSigSender]=useState(null);
  const [sigSenderName,setSigSenderName]=useState('');
  const [sigDriver,setSigDriver]=useState(null);
  // تحویل مقصد
  const [deliveryStep,setDeliveryStep]=useState(0);
  const [sigReceiver,setSigReceiver]=useState(null);
  const [sigReceiverName,setSigReceiverName]=useState('');
  const [deliveryOk,setDeliveryOk]=useState(null); // null | true | false
  const [damageNote,setDamageNote]=useState('');
  const [deliveryPhotos,setDeliveryPhotos]=useState([null,null,null,null]);
  const [activeLoad,setActiveLoad]=useState(null); // بار قبول شده
  const [policeQRLoad,setPoliceQRLoad]=useState(null); // بار انتخاب‌شده برای نمایش QR پلیس
  const [trackedLoads,setTrackedLoads]=useState([]);
const [trackingProgress,setTrackingProgress]=useState({});
const [selectedTrackLoad,setSelectedTrackLoad]=useState(null);
const [realTrackingLoad,setRealTrackingLoad]=useState(null);
  const close=()=>setModal(null);
  const toggleCV=k=>setCompanyVehicles(p=>p.includes(k)?p.filter(x=>x!==k):[...p,k]);
  const toggleCC=k=>setCompanyCargo(p=>p.includes(k)?p.filter(x=>x!==k):[...p,k]);

  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const payToken=params.get('pay');
    const verifyRef=params.get('verify');
    if(verifyRef){
      setVerifyLoadRef(verifyRef);
      fetch('https://oqnrxrowiewwrtroegwl.supabase.co/functions/v1/get-verify-info?token='+encodeURIComponent(verifyRef),{
        headers:{
          'apikey':'sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Authorization':'Bearer sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
        },
      }).then(r=>r.json()).then(data=>{
        if(data.error) setVerifyError(data.error);
        else setVerifyData(data);
        setVerifyLoading(false);
      }).catch(()=>{setVerifyError('network_error');setVerifyLoading(false);});
    }
    if(payToken){
      setPayerToken(payToken);
      supabase.from('loads').select('load_ref,from_city,to_city,price,total_weight,status,payer_name').eq('payment_token',payToken).maybeSingle().then(({data,error})=>{
        if(error){console.error('خطا در خواندن بار برای پرداخت:',error);}
        setPayerLoad(data||null);
        setPayerLoadLoading(false);
      });
    }else{
      setPayerLoadLoading(false);
    }
  },[]);

  const [locConsent,setLocConsent]=useState(()=>{try{return localStorage.getItem('euload_loc_consent');}catch(e){return null;}});
  const [showLocDisclosure,setShowLocDisclosure]=useState(false);
  const locResolveRef=useRef(null);
  const [ready,setReady]=useState(()=>{try{return localStorage.getItem('euload_ready')||'free';}catch(e){return 'free';}});
  const setReadyState=(v)=>{ try{localStorage.setItem('euload_ready',v);}catch(e){} setReady(v); };
  const refreshTripState=async()=>{ try{ const {data:{user:cu}}=await supabase.auth.getUser(); if(!cu) return; const {data:act}=await supabase.from('loads').select('load_ref').eq('requested_by',cu.id).eq('status','assigned'); const refs=(act||[]).map(r=>r.load_ref); if(refs.length===0){setAvail('available');return;} const {data:sh}=await supabase.from('shipments').select('load_ref').in('load_ref',refs).limit(1); setAvail(sh&&sh.length>0?'busy':'available'); }catch(e){console.error('refreshTripState error:',e);} };
  const askLocationConsent=()=>new Promise(resolve=>{ if(locConsent==='yes'){resolve(true);return;} locResolveRef.current=resolve; setShowLocDisclosure(true); });
  const answerLocationConsent=(ok)=>{ const v=ok?'yes':'no'; try{localStorage.setItem('euload_loc_consent',v);}catch(e){} setLocConsent(v); setShowLocDisclosure(false); if(ok&&!locResolveRef.current&&navigator.geolocation){navigator.geolocation.getCurrentPosition(()=>{},()=>{});} if(locResolveRef.current){locResolveRef.current(ok);locResolveRef.current=null;} };
  useEffect(()=>{ if((userRole==='driver'||userRole==='carrier')&&userStep===STEP.APPROVED&&!locConsent){ askLocationConsent(); } },[userRole,userStep]);
useEffect(()=>{
  if(trackedLoads.length===0) return;
  const id=setInterval(()=>{
    setTrackingProgress(prev=>{
      const next={...prev};
      trackedLoads.forEach(l=>{
        const cur=next[l.id]||0;
        if(cur<1) next[l.id]=Math.min(1,cur+0.005);
      });
      return next;
    });
  },500);
  return()=>clearInterval(id);
},[trackedLoads]);
  const resetAuth=()=>{
    setScreen('user_type');setIsLogin(false);
    setDriverPhone('');setDriverOtp('');setDriverPass('');setDriverName('');
    setDriverVehicle(null);setDriverPlate('');setDriverCapacity('');
    setCompanyEmail('');setCompanyEmailOtp('');setCompanyPass('');setCompanyPass2('');
    setCompanyName('');setCompanyReg('');setCompanyVat('');setCompanyActivity('');
    setCompanyVehicles([]);setCompanyCargo([]);setCompanyPlates([]);setCompanyDocFile(null);setCompanyDocUploading(false);setLoginContact('');setLoginPass('');
  };

  const resetNewLoad=()=>{
    setNewLoadStep(0);setNlFrom('');setNlTo('');setNlDate('');setNlTime('');
    setNlCargoType('');setNlPkgType('pallet');setNlPallets('');setNlPalletWeight('');
    setNlLength('');setNlWidth('');setNlHeight('');setNlWeight('');
    setNlVehicle('');setNlPrice('');setNlCargoValue('');setNlInsured(true);setNlInsuranceMode('none');setNlNotes('');setNlReceiverName('');
    setNlPayerType('sender');setNlPayerEmail('');setNlPayerName('');
    setNlPickupAddress('');setNlDeliveryAddress('');
    setNlPickupCoords(null);setNlDeliveryCoords(null);
    setPickupAddrStatus('idle');setDeliveryAddrStatus('idle');
    setNlFromPostal('');setNlToPostal('');
    setNlFromResults([]);setNlToResults([]);
    setNlFromSelected(null);setNlToSelected(null);
    setFromPostalStatus('idle');setToPostalStatus('idle');
    setNlSubmitting(false);
    setNlGeneratedRef(null);
  };

  const resetAccept=()=>{
    setAcceptStep(userRole==='carrier'?-1:userRole==='driver'?-2:0);setInsHasPolicy(null);setInsPolicyNum('');setInsCompany('');setInsExpiry('');
    setInsEuloadPaid(false);setPhotos([null,null,null,null]);
    setSigSender(null);setSigSenderName('');setSigDriver(null);
  };

  const resetDelivery=()=>{
    setDeliveryStep(0);setSigReceiver(null);setSigReceiverName('');
    setDeliveryOk(null);setDamageNote('');setDeliveryPhotos([null,null,null,null]);
  };

  // ── اعتبارسنجی آدرس دقیق (خیابان و پلاک) با جستجوی ساختاریافته‌ی Nominatim ──
  // به‌جای فرستادن یک متن قاطی، خیابان/شهر/کشور را جداگانه می‌فرستد — دقت بسیار بالاتر
  const geocodeStreetAddress=async(street,selected,setCoords,setStatus)=>{
    if(!street||street.trim().length<3){setStatus('idle');setCoords(null);return;}
    if(!selected){setStatus('idle');setCoords(null);return;}
    setStatus('checking');
    try{
      const params=new URLSearchParams({
        street:street,
        city:selected.city||'',
        country:selected.country||'',
        format:'json',
        addressdetails:'1',
        limit:'1',
      });
      const res=await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`,{
        headers:{'Accept-Language':'de'}
      });
      const data=await res.json();
      if(data&&data.length>0){
        setCoords({lat:parseFloat(data[0].lat),lng:parseFloat(data[0].lon)});
        setStatus('valid');
      }else{
        setCoords(null);
        setStatus('invalid');
      }
    }catch(err){
      console.error('خطا در اعتبارسنجی آدرس:',err);
      setCoords(null);
      setStatus('invalid');
    }
  };

  // ── فراخوانی خودکار با تاخیر ۱ ثانیه بعد از توقف تایپ (مبدا) ──
  useEffect(()=>{
    const timer=setTimeout(()=>{
      geocodeStreetAddress(nlPickupAddress,nlFromSelected,setNlPickupCoords,setPickupAddrStatus);
    },1000);
    return()=>clearTimeout(timer);
  },[nlPickupAddress,nlFromSelected]);
  // ── فراخوانی خودکار با تاخیر ۱ ثانیه بعد از توقف تایپ (آدرس شرکت هنگام ثبت‌نام) ──
  useEffect(()=>{
    const timer=setTimeout(()=>{
      geocodeStreetAddress(companyStreetAddress,companySelectedCity,setCompanyCoords,setCompanyGeoStatus);
    },1000);
    return()=>clearTimeout(timer);
  },[companyStreetAddress,companySelectedCity]);

  // ── فراخوانی خودکار با تاخیر ۱ ثانیه بعد از توقف تایپ (مقصد) ──
  useEffect(()=>{
    const timer=setTimeout(()=>{
      geocodeStreetAddress(nlDeliveryAddress,nlToSelected,setNlDeliveryCoords,setDeliveryAddrStatus);
    },1000);
    return()=>clearTimeout(timer);
  },[nlDeliveryAddress,nlToSelected]);

  // ── جستجوی کد پستی با Nominatim (چند گزینه برمی‌گرداند) ──
  const searchPostalCode=async(postalCode,setResults,setStatus)=>{
    if(!postalCode||postalCode.trim().length<3){setStatus('idle');setResults([]);return;}
    setStatus('checking');
    try{
      const EU_COUNTRY_CODES='de,at,ch,fr,nl,be,lu,pl,cz,dk,se,no,fi,it,es,pt,gb,ie,hu,ro,bg,hr,si,sk,lt,lv,ee,gr,tr,ua';
      const res=await fetch(`https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(postalCode)}&format=json&addressdetails=1&limit=5&countrycodes=${EU_COUNTRY_CODES}`,{
        headers:{'Accept-Language':'de'}
      });
      const data=await res.json();
      if(data&&data.length>0){
        const options=data.map(d=>({
          label:[d.address?.city||d.address?.town||d.address?.village||d.address?.municipality||(d.display_name||'').split(',')[0],
                 d.address?.state,
                 d.address?.country].filter(Boolean).join(', '),
          city:d.address?.city||d.address?.town||d.address?.village||d.address?.municipality||(d.display_name||'').split(',')[0],
          state:d.address?.state||'',
          country:d.address?.country||'',
          lat:parseFloat(d.lat),
          lng:parseFloat(d.lon),
        }));
        setResults(options);
        setStatus('multiple');
      }else{
        setResults([]);
        setStatus('invalid');
      }
    }catch(err){
      console.error('خطا در جستجوی کد پستی:',err);
      setResults([]);
      setStatus('invalid');
    }
  };

  // ── فراخوانی خودکار جستجوی کد پستی با تاخیر ۱ ثانیه (مبدا) ──
  useEffect(()=>{
    setNlFromSelected(null);
    const timer=setTimeout(()=>{
      searchPostalCode(nlFromPostal,setNlFromResults,setFromPostalStatus);
    },1000);
    return()=>clearTimeout(timer);
  },[nlFromPostal]);
  // ── فراخوانی خودکار جستجوی کد پستی با تاخیر ۱ ثانیه (شرکت هنگام ثبت‌نام) ──
  useEffect(()=>{
    setCompanySelectedCity(null);
    const timer=setTimeout(()=>{
      searchPostalCode(companyPostal,setCompanyPostalResults,setCompanyPostalStatus);
    },1000);
    return()=>clearTimeout(timer);
  },[companyPostal]);

  // ── فراخوانی خودکار جستجوی کد پستی با تاخیر ۱ ثانیه (مقصد) ──
  useEffect(()=>{
    setNlToSelected(null);
    const timer=setTimeout(()=>{
      searchPostalCode(nlToPostal,setNlToResults,setToPostalStatus);
    },1000);
    return()=>clearTimeout(timer);
  },[nlToPostal]);

  // ── انتخاب دستی کاربر از لیست گزینه‌های کد پستی ──
  const selectFromPostal=(option)=>{
    setNlFromSelected(option);
    setNlFrom(option.city);
    setFromPostalStatus('confirmed');
  };
  const selectToPostal=(option)=>{
    setNlToSelected(option);
    setNlTo(option.city);
    setToPostalStatus('confirmed');
  };

// ── ذخیره راننده در دیتابیس ──
// ── خواندن بارها از دیتابیس ──
// ── گرفتن تاریخ و زمان از سرور (نه از دستگاه کاربر) ──
// ── تغییر رمز عبور کاربر ──
// ── آپلود یک مدرک شرکت/راننده به Storage ──
const uploadCompanyDoc=async(docLabel,file)=>{
  if(!file||!authUser) return;
  setDocUploading(p=>({...p,[docLabel]:true}));
  try{
    const ext=file.name.split('.').pop();
    const safeLabel=docLabel.toLowerCase().replace(/[^a-z0-9]/g,'_');
    const path=`${authUser.id}/${safeLabel}.${ext}`;
    const {error}=await supabase.storage.from('company-documents').upload(path,file,{upsert:true});
    if(error){
      console.error('خطا در آپلود مدرک:',error);
      alert('خطا در آپلود: '+error.message);
      setDocUploading(p=>({...p,[docLabel]:false}));
      return;
    }
    setDocFiles(p=>({...p,[docLabel]:path}));
    setDocs(p=>{
      const updated=p.includes(docLabel)?p:[...p,docLabel];
      supabase.from('users').update({uploaded_docs:updated,needs_review:true}).eq('auth_id',authUser.id).then(({error})=>{
        if(error) console.error('خطا در ذخیره وضعیت مدارک:',error);
      });
      return updated;
    });
  }catch(err){
    console.error('خطای کلی uploadCompanyDoc:',err);
  }
  setDocUploading(p=>({...p,[docLabel]:false}));
};

const changePassword=async()=>{
  try{
    const {error}=await supabase.auth.updateUser({password:newPassword});
    if(error){console.error('خطا در تغییر رمز:',error);alert('خطا: '+error.message);return;}
    alert(t(lang,'passwordChangedSuccess'));
    setNewPassword('');setNewPassword2('');setShowProfile(false);
  }catch(err){console.error('خطای کلی changePassword:',err);}
};
const getServerTime=async()=>{
  try{
    const {data,error}=await supabase.rpc('get_server_time');
    if(error){console.error('خطا در گرفتن زمان سرور:',error);return null;}
    return data;
  }catch(err){console.error('خطای کلی getServerTime:',err);return null;}
};
// ── آزادسازی بی‌صدای بارهای رزروشده‌ی راکد (بیش از ۱۲ ساعت assigned و بدون اقدام) ──
const RESERVE_TIMEOUT_HOURS=12; // مهلت (ساعت) - قابل تنظیم
const releaseStaleLoads=async()=>{
  try{
    const cutoff=new Date(Date.now()-RESERVE_TIMEOUT_HOURS*60*60*1000).toISOString();
    const {error}=await supabase.from('loads').update({
      status:'open',
      requested_by:null,
      requested_driver_name:null,
      requested_driver_plate:null,
      requested_driver_phone:null,
      assigned_at:null,
    }).eq('status','assigned').lt('assigned_at',cutoff);
    if(error){console.error('خطا در آزادسازی بارهای راکد:',error);}
  }catch(err){console.error('خطای کلی releaseStaleLoads:',err);}
};
const loadLoadsFromDb=async()=>{
  console.log('شروع loadLoadsFromDb');
  try{
    await releaseStaleLoads();
    const {data:{user:currentUser}}=await supabase.auth.getUser();
    const myId=currentUser?.id||null;
    const {data,error}=await supabase.from('loads_public').select('*').order('created_at',{ascending:false});
    if(error){console.error('خطا در خواندن بارها:',error);return;}
    const formatted=data.map(l=>({
      id:l.id,
      loadRef:l.load_ref,
      shipper:l.shipper_name||'—',
      from:l.from_city,
      to:l.to_city,
      receiverName:l.receiver_name,
      pickupAddress:l.pickup_address,
      deliveryAddress:l.delivery_address,
      pickupLat:l.pickup_lat,
      pickupLng:l.pickup_lng,
      deliveryLat:l.delivery_lat,
      deliveryLng:l.delivery_lng,
      price:l.price,
      weight:l.total_weight,
      type:VEHICLE_TYPES.find(v=>v.k===l.vehicle_type)?.name||'Curtainside',
      distance:l.distance,
      depart:(l.depart_date||'')+' '+(l.depart_time||''),
      arrive:'TBD',
      insured:l.insured,
      notes:l.notes,
      isFromDb:true,
      createdAt:l.created_at,
      isMine:l.is_mine===true,
      status:l.status,
      requested_by:l.requested_by,
      requested_driver_name:l.requested_driver_name,
      requested_driver_plate:l.requested_driver_plate,
      requested_driver_phone:l.requested_driver_phone,
    }));
    setDbLoads(formatted);
    
    console.log('بارها از دیتابیس خونده شد:',formatted.length);
  }catch(err){console.error('خطای کلی:',err);}
};

useEffect(()=>{
  loadLoadsFromDb();
  getServerTime().then(d=>{if(d) setServerDate(new Date(d));});
},[]);

// ── رفرش خودکار لیست بارها هر ۱۵ ثانیه (بدون نیاز به رفرش دستی صفحه) ──
useEffect(()=>{
  const interval=setInterval(()=>{
    loadLoadsFromDb();
  },15000);
  return()=>clearInterval(interval);
},[]);
// ── ارسال خودکار موقعیت GPS راننده (نیتیو در پس‌زمینه، یا مرورگر به‌عنوان جایگزین) فقط وقتی در حال حمل بار است ──
useEffect(()=>{
  if(userRole==='shipper'||avail!=='busy') return; if(locConsent!=='yes'){ if(!locConsent) askLocationConsent(); return; }

  let watcherId=null;
  let fallbackInterval=null;
  const isNative=!!(window.Capacitor&&window.Capacitor.isNativePlatform&&window.Capacitor.isNativePlatform());

  if(isNative){
    BackgroundGeolocation.addWatcher(
      {
        backgroundMessage:'موقعیت شما برای ردیابی بار در حال ارسال است.',
        backgroundTitle:'EU.LOAD - در حال حمل بار',
        requestPermissions:true,
        stale:false,
        distanceFilter:50, // هر ۵۰ متر جابجایی، یک به‌روزرسانی بفرست
      },
      (location,error)=>{
        if(error){console.error('خطای BackgroundGeolocation:',error);return;}
        if(location){ sendLocationToDb(location.latitude,location.longitude); sendHeartbeat(false); }
      }
    ).then(id=>{ watcherId=id; });
  }else{
    // ── نسخه‌ی مرورگر (fallback) — فقط وقتی اپ به‌صورت وب باز است ──
    updateDriverLocation();
    fallbackInterval=setInterval(()=>{ updateDriverLocation(); },30000);
  }

  return()=>{
    if(watcherId) BackgroundGeolocation.removeWatcher({id:watcherId});
    if(fallbackInterval) clearInterval(fallbackInterval);
  };
},[avail,userRole,locConsent]);
// ── خواندن موقعیت زنده راننده برای یک بار مشخص (برای شرکت) ──
const fetchRealTrackingLocation=async(loadRef)=>{
  try{
    const {data,error}=await supabase.from('loads').select('load_ref,from_city,to_city,driver_lat,driver_lng,location_updated_at,requested_driver_name,requested_driver_plate,requested_driver_phone').eq('load_ref',loadRef).single();
    if(error){console.error('خطا در خواندن موقعیت:',error);return;}
    setRealTrackingLoad(data);
  }catch(err){console.error('خطای کلی fetchRealTrackingLocation:',err);}
};
// ── ثبت‌نام جدید با Email + Password ──
const authSignUp=async(email,password)=>{
  try{
    const {data,error}=await supabase.auth.signUp({email,password});
    if(error){
      console.error('signUp error:',error);
      alert((lang==='de'?'Fehler bei der Registrierung: ':'Registration error: ')+error.message);
      return null;
    }
    console.log('ثبت‌نام موفق:',data.user);
    return data.user;
  }catch(err){
    console.error('خطای کلی signUp:',err);
    return null;
  }
};

// ── ورود با Email + Password ──
// == Single-device session lock ==
const getDeviceId=()=>{ let id=null; try{ id=localStorage.getItem('euload_device_id'); }catch(e){} if(!id){ id=(window.crypto&&window.crypto.randomUUID)?window.crypto.randomUUID():('dev-'+Date.now()+'-'+Math.random().toString(36).slice(2,12)); try{ localStorage.setItem('euload_device_id',id); }catch(e){} } return id; };
const forceLocalLogout=async(msg)=>{ try{ await supabase.auth.signOut({scope:'local'}); }catch(e){} setAuthUser(null); setUserStep(STEP.GUEST); setUserRole(null); setCarrierPlates([]); setAvail('available'); setActiveLoad(null); setMyActiveLoads([]); if(msg) alert(msg); };
const sendHeartbeat=async(force)=>{ const now=Date.now(); if(!force&&now-(window.__euloadLastHb||0)<55000) return; window.__euloadLastHb=now; try{ const {data,error}=await supabase.rpc('session_heartbeat',{p_device_id:getDeviceId()}); if(error){console.error('heartbeat error:',error);return;} if(data===false){ await forceLocalLogout(lang==='de'?'Ihre Sitzung wurde beendet. Bitte melden Sie sich erneut an.':'Your session has ended. Please log in again.'); } }catch(e){console.error('heartbeat error:',e);} };
const authSignIn=async(email,password)=>{
  try{
    const {data,error}=await supabase.auth.signInWithPassword({email,password});
    if(error){
      console.error('خطا در ورود:',error);
      alert(t(lang,'loginErrorPrefix')+error.message);
      return null;
    }
    console.log('ورود موفق:',data.user);
    setAuthUser(data.user);
    return data.user;
  }catch(err){
    console.error('خطای کلی signIn:',err);
    return null;
  }
};
// ── خواندن نقش کاربر از دیتابیس بعد از login ──
const loadUserRole=async(authId)=>{
  try{
    const {data,error}=await supabase.from('users').select('role,full_name,vehicle_capacity,vehicle_type,vehicle_plate,company_name,uploaded_docs,is_approved,status,customer_number,company_lat,company_lng,company_address').eq('auth_id',authId).single();
    if(error){console.error('خطا در خواندن نقش:',error);return;}
    if(data){
      if(data.role!=='admin'){ const {data:cs,error:csErr}=await supabase.rpc('claim_session',{p_device_id:getDeviceId()}); if(csErr){console.error('claim_session error:',csErr);} else if(cs==='blocked'){ await forceLocalLogout(lang==='de'?'Dieses Konto ist bereits auf einem anderen Ger\u00e4t aktiv.':'This account is already active on another device.'); return; } }
      setUserRole(data.role);
      if(data.full_name) setDriverName(data.full_name);
      if(data.customer_number) setCustomerNumber(data.customer_number);
      if(data.company_name) setCompanyName(data.company_name);
      if(data.uploaded_docs) setDocs(data.uploaded_docs);
      
      if(data.vehicle_capacity) setDriverCapacity(data.vehicle_capacity);
      if(data.vehicle_type) setDriverVehicle(data.vehicle_type);
      if(data.vehicle_plate) setDriverPlate(data.vehicle_plate);
      if(data.company_lat) setMyCompanyLat(data.company_lat);
      if(data.company_lng) setMyCompanyLng(data.company_lng);
      if(data.role==='admin'){
        setUserStep(STEP.APPROVED);
        loadPendingUsers();
      }else if(data.status==='rejected'){
        setUserStep(STEP.DOCS_SENT);
        alert(lang==='de'?'Ihr Konto wurde leider nicht freigegeben. Bitte kontaktieren Sie den Support.':'Unfortunately your account was not approved. Please contact support.');
      }else if(data.is_approved){
        setUserStep(STEP.APPROVED);
        loadLoadsFromDb();
        if(data.role==='carrier') loadCarrierPlates();
        if(data.role==='driver'||data.role==='carrier') refreshTripState();
      }else{
        setUserStep(STEP.DOCS_SENT);
      }
      console.log('نقش کاربر:',data.role,'تأیید‌شده:',data.is_approved);
    }
  }catch(err){console.error('خطای کلی loadUserRole:',err);}
};
// ── خروج ──
const authSignOut=async()=>{
  try{
    try{ await supabase.rpc('release_session',{p_device_id:getDeviceId()}); }catch(e){console.error('release_session error:',e);}
    const {error}=await supabase.auth.signOut({scope:'local'});
    if(error){console.error('خطا در خروج:',error);return;}
    setAuthUser(null);
    setUserStep(STEP.GUEST);
    setUserRole(null);
    setCarrierPlates([]); setAvail('available'); setActiveLoad(null); setMyActiveLoads([]);
    console.log('خروج موفق');
  }catch(err){console.error('خطای کلی signOut:',err);}
};

// ── چک کردن session موقع باز شدن اپ ──
useEffect(()=>{
  const checkSession=async()=>{
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(session){
        setAuthUser(session.user);
        console.log('کاربر قبلاً login شده:',session.user.email);
        await loadUserRole(session.user.id);
      }
      setAuthLoading(false);
    }catch(err){
      console.error('خطا در چک session:',err);
      setAuthLoading(false);
    }
  };
  checkSession();
  // گوش دادن به تغییرات auth
  const {data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
    if(session){setAuthUser(session.user);}
    else{setAuthUser(null);}
  });
  return ()=>subscription.unsubscribe();
},[]);
// ── رفرش خودکار موقعیت واقعی راننده هر ۱۵ ثانیه، وقتی مودال ردیابی واقعی باز است ──
useEffect(()=>{
  if(modal!=='real_tracking'||!realTrackingLoad?.load_ref) return;
  const interval=setInterval(()=>{
    fetchRealTrackingLocation(realTrackingLoad.load_ref);
  },15000);
  return()=>clearInterval(interval);
},[modal,realTrackingLoad?.load_ref]);
// == Session heartbeat every 60s + on app resume ==
useEffect(()=>{ if(!authUser||!userRole||userRole==='admin') return; sendHeartbeat(true); const iv=setInterval(()=>sendHeartbeat(false),60000); const onVis=()=>{ if(document.visibilityState==='visible') sendHeartbeat(true); }; document.addEventListener('visibilitychange',onVis); return()=>{ clearInterval(iv); document.removeEventListener('visibilitychange',onVis); }; },[authUser,userRole]);
const findCarrierByPlate=async(plate)=>{ try{ const {data,error}=await supabase.from('carrier_vehicles').select('carrier_auth_id').ilike('plate_number',plate.trim()).eq('is_active',true).limit(1); if(error){console.error('خطا در جستجوی پلاک:',error);return null;} if(!data||data.length===0) return null; const carrierId=data[0].carrier_auth_id; const {data:carrierUser,error:carrierErr}=await supabase.from('users').select('auth_id,is_approved').eq('auth_id',carrierId).eq('role','carrier').eq('is_approved',true).maybeSingle(); if(carrierErr||!carrierUser) return null; return carrierId; }catch(err){console.error('خطای کلی findCarrierByPlate:',err);return null;} };

const saveDriverToDb=async(authId)=>{
  try{
    const {data,error}=await supabase.from('users').insert([{
      role:'driver',
      auth_id:authId,
      full_name:driverName,
      phone:driverPhone,
      vehicle_type:driverVehicle,
      vehicle_plate:driverPlate,
      vehicle_capacity:parseFloat(driverCapacity)||null,
      is_approved:false,
      status:'available',
    }]).select();
    if(error){
      console.error('خطا در ذخیره راننده:',error);
      alert('خطا در ذخیره: '+error.message);
      return null;
    }
    console.log('راننده ذخیره شد:',data);
    return data[0];
  }catch(err){
    console.error('خطای کلی:',err);
    return null;
  }
};

// ── ذخیره بار در دیتابیس ──
const saveLoadToDb=async(load)=>{
  try{
    const {data:{user:currentUser}}=await supabase.auth.getUser();
    const myId=currentUser?.id||null;
    const {data,error}=await supabase.from('loads').insert([{
      load_ref:load.loadRef,
      shipper_name:load.shipper,
      shipper_auth_id:myId,
      from_city:load.from,
      to_city:load.to,
      receiver_name:load.receiverName,
      pickup_address:nlPickupAddress||null,
      delivery_address:nlDeliveryAddress||null,
      pickup_lat:nlFromSelected?.lat||null,
      pickup_lng:nlFromSelected?.lng||null,
      delivery_lat:nlToSelected?.lat||null,
      delivery_lng:nlToSelected?.lng||null,
      pickup_lat:nlPickupCoords?.lat||null,
      pickup_lng:nlPickupCoords?.lng||null,
      delivery_lat:nlDeliveryCoords?.lat||null,
      delivery_lng:nlDeliveryCoords?.lng||null,
      depart_date:nlDate,
      depart_time:nlTime,
      cargo_type:nlCargoType,
      pkg_type:load.pkgType,
      pallets:parseInt(load.pallets)||null,
      pallet_weight:parseFloat(load.palletWeight)||null,
      length_cm:parseFloat(load.length)||null,
      width_cm:parseFloat(load.width)||null,
      height_cm:parseFloat(load.height)||null,
      total_weight:load.weight,
      vehicle_type:nlVehicle,
      price:load.price,
      cargo_value:parseFloat(nlCargoValue)||null,
      insurance_mode:nlInsuranceMode,
      payer_type:nlPayerType,
      payer_email:nlPayerEmail||null,
      payer_name:nlPayerName||null,
      distance:load.distance,
      insured:load.insured,
      notes:load.notes,
      status:load.initialStatus||'open',
    }]).select();
    if(error){
      console.error('خطا در ذخیره بار:',error);
      alert('خطا در ذخیره بار: '+error.message);
      return null;
    }
    console.log('بار ذخیره شد:',data);
    return data[0];
  }catch(err){
    console.error('خطای کلی:',err);
    return null;
  }
};
// ── خواندن لیست پلاک‌های فعال شرکت حمل‌ونقل (برای کشوی انتخاب در AcceptLoadModal) ──
const [carrierPlates,setCarrierPlates]=useState([]);
  const [newProfilePlate,setNewProfilePlate]=useState('');
const loadCarrierPlates=async()=>{
  try{
    const {data:{user:cu}}=await supabase.auth.getUser();
    const myId=cu?.id;
    if(!myId) return;
    const {data,error}=await supabase.from('carrier_vehicles').select('plate_number').eq('carrier_auth_id',myId).eq('is_active',true);
    if(error){console.error('خطا در خواندن پلاک‌ها:',error);return;}
    setCarrierPlates((data||[]).map(r=>r.plate_number));
  }catch(err){console.error('خطای کلی loadCarrierPlates:',err);}
};

// ── افزودن پلاک جدید به ناوگان شرکت (از پروفایل) ──
const addCarrierPlate=async(plate)=>{
  if(!plate||plate.trim().length<3) return;
  const trimmedPlate=plate.trim();
  if(carrierPlates.some(p=>p.toLowerCase()===trimmedPlate.toLowerCase())){
    alert(lang==='de'?'Dieses Kennzeichen wurde bereits hinzugefügt.':'This plate number has already been added.');
    return;
  }
  try{
    const {data:{user:cu}}=await supabase.auth.getUser();
    const myId=cu?.id;
    if(!myId) return;
    const {error}=await supabase.from('carrier_vehicles').insert([{carrier_auth_id:myId,plate_number:trimmedPlate}]);
    if(error){console.error('خطا در افزودن پلاک:',error);alert('خطا: '+error.message);return;}
    await loadCarrierPlates();
  }catch(err){console.error('خطای کلی addCarrierPlate:',err);}
};

// ── حذف (غیرفعال کردن) پلاک از ناوگان شرکت ──
const removeCarrierPlate=async(plate)=>{
  try{
    const {data:{user:cu}}=await supabase.auth.getUser();
    const myId=cu?.id;
    if(!myId) return;
    const {error}=await supabase.from('carrier_vehicles').update({is_active:false}).eq('carrier_auth_id',myId).eq('plate_number',plate);
    if(error){console.error('خطا در حذف پلاک:',error);return;}
    await loadCarrierPlates();
  }catch(err){console.error('خطای کلی removeCarrierPlate:',err);}
};

// ── خواندن اطلاعات راننده‌ای که بار را قبول کرده (برای فرستنده) ──
const getShipmentForLoad=async(loadRef)=>{
  try{
    const {data,error}=await supabase.from('shipments').select('*').eq('load_ref',loadRef).order('created_at',{ascending:false}).limit(1);
    if(error){console.error('خطا در خواندن shipment:',error);return null;}
    return data&&data.length>0?data[0]:null;
  }catch(err){console.error('خطای کلی getShipmentForLoad:',err);return null;}
};
// ── راننده برای یک بار درخواست می‌دهد ──
// ── خواندن بارهای فعال راننده (pending یا assigned که خودش درخواست داده) ──
const getMyActiveLoads=async()=>{
  try{
    const {data:{user:cu}}=await supabase.auth.getUser();
    const myId=cu?.id;
    if(!myId) return [];
    const {data,error}=await supabase.from('loads').select('*').eq('requested_by',myId).in('status',['pending','assigned']);
    if(error){console.error('خطا در خواندن بارهای فعال:',error);return [];}
    return data||[];
  }catch(err){console.error('خطای کلی getMyActiveLoads:',err);return [];}
};
// ── تحویل: همه بارهای فعال راننده را delivered کن ──
// ── باز کردن نمای بارهای من (مرتب‌شده به ترتیب تحویل) ──
const openMyLoads=async()=>{
  const loads=await getMyActiveLoads();
  // مرتب‌سازی بر اساس فاصله مبدا تا مقصد (کوتاه‌تر = اول تحویل)
  const sorted=[...loads].sort((a,b)=>cityDistance(a.from_city,a.to_city)-cityDistance(b.from_city,b.to_city));
  setMyActiveLoads(sorted);
  // خواندن تاریخچه بارهای تحویل‌شده راننده
  try{
    const {data:{user:cu}}=await supabase.auth.getUser();
    const myId=cu?.id;
    if(myId){
      const {data}=await supabase.from('loads').select('*').eq('requested_by',myId).in('status',['delivered','awaiting_confirmation']).order('delivered_at',{ascending:false}).limit(50);
      
      setMyDeliveredLoads(data||[]);
    }
  }catch(err){console.error('خطا در خواندن تاریخچه راننده:',err);}
  setShowMyLoads(true);
};
// ── باز کردن داشبورد شرکت با همه بارها (شامل تحویل‌شده) ──
const openShipperLoads=async()=>{
  try{
    const {data:{user:cu}}=await supabase.auth.getUser();
    const myId=cu?.id;
    if(myId){
      const {data}=await supabase.from('loads').select('*').eq('shipper_auth_id',myId).order('created_at',{ascending:false}).limit(100);
      setShipperAllLoads(data||[]);
    }
  }catch(err){console.error('خطا در خواندن بارهای شرکت:',err);}
  setShowShipperLoads(true);
};
// ── خواندن مدرک تحویل (امضا، وضعیت، عکس‌ها) برای شرکت ──
// == Admin: active sessions ==
const loadActiveSessions=async()=>{ try{ const {data,error}=await supabase.rpc('admin_list_sessions'); if(error){console.error('admin_list_sessions error:',error);return;} setActiveSessions(data||[]); }catch(e){console.error('loadActiveSessions error:',e);} };
const releaseUserSession=async(uid,name)=>{ if(!window.confirm((lang==='de'?'Sitzung freigeben f\u00fcr ':'Release session for ')+(name||uid)+'?')) return; try{ const {error}=await supabase.rpc('admin_release_session',{p_user_id:uid}); if(error){alert('Error: '+error.message);return;} await loadActiveSessions(); }catch(e){console.error('releaseUserSession error:',e);} };
const loadPendingUsers=async()=>{ loadActiveSessions();
  setAdminLoading(true);
  try{
    const {data,error}=await supabase.from('users').select('*').or('is_approved.eq.false,needs_review.eq.true').neq('role','admin').order('created_at',{ascending:true});
    if(error){console.error('خطا در خواندن کاربران در انتظار:',error);setAdminLoading(false);return;}
    setPendingUsers(data||[]);
  }catch(err){console.error('خطای کلی loadPendingUsers:',err);}
  setAdminLoading(false);
};

const openAdminUserDocs=async(user)=>{
  setAdminSelectedUser(user);
  setAdminUserDocUrls({});
  try{
    const docs=user.uploaded_docs||[];
    const urls={};
    for(const docLabel of docs){
      const safeLabel=docLabel.toLowerCase().replace(/[^a-z0-9]/g,'_');
      const {data:fileList,error:listErr}=await supabase.storage.from('company-documents').list(user.auth_id);
      console.log('🔍 DEBUG fileList برای',user.auth_id,':',fileList,'error:',listErr);
      if(listErr){console.error('خطا در لیست مدارک:',listErr);continue;}
      const match=(fileList||[]).find(f=>f.name.startsWith(safeLabel));
      if(match){
        const path=`${user.auth_id}/${match.name}`;
        const {data:signed,error:signErr}=await supabase.storage.from('company-documents').createSignedUrl(path,3600);
        if(!signErr&&signed) urls[docLabel]=signed.signedUrl;
      }
    }
    setAdminUserDocUrls(urls);
  }catch(err){console.error('خطای کلی openAdminUserDocs:',err);}
};

const approveUser=async(user)=>{
  if(!window.confirm(lang==='de'?'Diesen Benutzer freigeben?':'Approve this user?')) return;
  try{
    const {error}=await supabase.from('users').update({is_approved:true,needs_review:false}).eq('id',user.id);
    if(error){console.error('خطا در تأیید کاربر:',error);alert('خطا: '+error.message);return;}
    setAdminSelectedUser(null);
    await loadPendingUsers();
  }catch(err){console.error('خطای کلی approveUser:',err);}
};

const rejectUser=async(user)=>{
  if(!window.confirm(lang==='de'?'Diesen Benutzer ablehnen? Dies kann nicht rückgängig gemacht werden.':'Reject this user? This cannot be undone.')) return;
  try{
    const {error}=await supabase.from('users').update({status:'rejected'}).eq('id',user.id);
    if(error){console.error('خطا در رد کاربر:',error);alert('خطا: '+error.message);return;}
    setAdminSelectedUser(null);
    await loadPendingUsers();
  }catch(err){console.error('خطای کلی rejectUser:',err);}
};


const openDeliveryConfirm=async(load)=>{
  try{
    const loadRef=load.load_ref||load.loadRef;
    const shipment=await getShipmentForLoad(loadRef);
    if(!shipment){alert(lang==='de'?'Kein Lieferbeleg gefunden.':'No delivery proof found.');return;}
    const {data:photoRows,error:photoErr}=await supabase.from('shipment_photos').select('*').eq('shipment_id',shipment.id).eq('document_type','delivery_photo');
    if(photoErr){console.error('خطا در خواندن عکس‌های تحویل:',photoErr);}
    const deliveryPhotoUrls=[];
    for(const row of (photoRows||[])){
      const {data:signed,error:signErr}=await supabase.storage.from('shipment-documents').createSignedUrl(row.photo_url,3600);
      if(!signErr&&signed) deliveryPhotoUrls.push(signed.signedUrl);
    }
    setConfirmDamageNote('');
    setDeliveryConfirmData({...shipment,deliveryPhotoUrls,loadRefRaw:loadRef,from:load.from_city||load.from,to:load.to_city||load.to});
    setShowShipperLoads(false);
    setShowDeliveryConfirm(true);
  }catch(err){console.error('خطای کلی openDeliveryConfirm:',err);}
};

const openShipmentDoc=async(load)=>{
  try{
    const loadRef=load.load_ref||load.loadRef;
    const shipment=await getShipmentForLoad(loadRef);
    if(!shipment){alert(lang==='de'?'Kein Dokument gefunden.':'No document found.');return;}
    const {data:photoRows,error:photoErr}=await supabase.from('shipment_photos').select('*').eq('shipment_id',shipment.id);
    if(photoErr){console.error('خطا در خواندن عکس‌ها:',photoErr);}
    const cargoPhotoUrls=[];
    const deliveryPhotoUrls=[];
    for(const row of (photoRows||[])){
      const {data:signed,error:signErr}=await supabase.storage.from('shipment-documents').createSignedUrl(row.photo_url,3600);
      if(!signErr&&signed){
        if(row.document_type==='cargo_photo') cargoPhotoUrls.push(signed.signedUrl);
        else if(row.document_type==='delivery_photo') deliveryPhotoUrls.push(signed.signedUrl);
      }
    }
    setShipmentDocData({...shipment,cargoPhotoUrls,deliveryPhotoUrls,from:load.from_city||load.from,to:load.to_city||load.to,weight:load.total_weight||load.weight,cargoType:load.cargo_type,loadStatus:load.status});
    setShowShipmentDoc(true);
  }catch(err){console.error('خطای کلی openShipmentDoc:',err);}
};

const openDeliveryProof=async(load)=>{
  try{
    const loadRef=load.load_ref||load.loadRef;
    const shipment=await getShipmentForLoad(loadRef);
    if(!shipment){alert(lang==='de'?'Kein Lieferbeleg gefunden.':'No delivery proof found.');return;}
    const {data:photoRows,error:photoErr}=await supabase.from('shipment_photos').select('*').eq('shipment_id',shipment.id);
    if(photoErr){console.error('خطا در خواندن عکس‌ها:',photoErr);}
    const cargoPhotoUrls=[];
    const deliveryPhotoUrls=[];
    for(const row of (photoRows||[])){
      const {data:signed,error:signErr}=await supabase.storage.from('shipment-documents').createSignedUrl(row.photo_url,3600);
      if(!signErr&&signed){
        if(row.document_type==='cargo_photo') cargoPhotoUrls.push(signed.signedUrl);
        else if(row.document_type==='delivery_photo') deliveryPhotoUrls.push(signed.signedUrl);
      }
    }
    setDeliveryProofData({...shipment,cargoPhotoUrls,deliveryPhotoUrls,from:load.from_city||load.from,to:load.to_city||load.to});
    setShowShipperLoads(false);
    setShowDeliveryProof(true);
  }catch(err){console.error('خطای کلی openDeliveryProof:',err);}
};
// ── لغو درخواست توسط راننده ──
const cancelMyRequest=async(loadRef)=>{
  if(!window.confirm(t(lang,'confirmCancelRequest'))) return;
  try{
    const {error}=await supabase.rpc('cancel_load_request',{p_load_ref:loadRef});
    if(error){console.error('خطا در لغو درخواست:',error);alert('خطا: '+error.message);return;}
    console.log('درخواست لغو شد:',loadRef);
    loadLoadsFromDb();
    openMyLoads(); // نمای بارهای من را تازه کن
  }catch(err){console.error('خطای کلی cancelMyRequest:',err);}
};
// ── ذخیره موقعیت زنده راننده روی بار در حال حمل ──
// ── ارسال یک نقطه‌ی موقعیت به دیتابیس (چه از مرورگر چه از پلاگین نیتیو) ──
const sendLocationToDb=async(lat,lng)=>{
  try{
    const {data:{user:cu}}=await supabase.auth.getUser();
    const myId=cu?.id;
    if(!myId) return;
    await supabase.from('loads').update({
      driver_lat:lat,
      driver_lng:lng,
      location_updated_at:new Date().toISOString(),
    }).eq('requested_by',myId).eq('status','assigned');
  }catch(err){console.error('خطای کلی sendLocationToDb:',err);}
};

// ── نسخه‌ی مرورگر (fallback) — برای وقتی اپ به‌صورت وب اجرا می‌شود، نه به‌عنوان اپ نیتیو ──
const updateDriverLocation=async()=>{
  if(!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(
    (pos)=>{ sendLocationToDb(pos.coords.latitude,pos.coords.longitude); },
    (err)=>{console.error('خطا در گرفتن موقعیت GPS:',err);},
    {enableHighAccuracy:true,timeout:10000}
  );
};
const confirmDeliveryHealth=async(load,isOk,note)=>{
  try{
    const loadRef=load.load_ref||load.loadRef;
    if(!loadRef){console.error('confirmDeliveryHealth: loadRef داده نشد');return;}
    const shipment=await getShipmentForLoad(loadRef);
    if(shipment){
      const {error:sigErr}=await supabase.from('shipments').update({
        delivery_ok:isOk,
        damage_note:note||null,
        status:'delivered',
      }).eq('id',shipment.id);
      if(sigErr){console.error('خطا در ثبت تأیید سلامت بار:',sigErr);}
    }
    const {error}=await supabase.from('loads').update({status:'delivered'}).eq('load_ref',loadRef);
    if(error){console.error('خطا در نهایی‌سازی بار:',error);return;}
    try{
      const releaseRes=await fetch('https://oqnrxrowiewwrtroegwl.supabase.co/functions/v1/release-payment',{
        method:'POST',
        headers:{
          'apikey':'sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Authorization':'Bearer sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Content-Type':'application/json',
        },
        body:JSON.stringify({ load_ref:loadRef }),
      });
      const releaseData=await releaseRes.json();
      if(releaseData.error){
        console.error('خطا در آزادسازی پرداخت:',releaseData.error);
      }else{
        console.log('پرداخت با موفقیت آزاد شد:',releaseData);
      }
    }catch(releaseErr){
      console.error('خطای کلی در فراخوانی release-payment:',releaseErr);
    }
    await loadLoadsFromDb();
    if(showShipperLoads) await openShipperLoads();
  }catch(err){console.error('خطای کلی confirmDeliveryHealth:',err);}
};

const completeDelivery=async(loadRef)=>{
  try{
    const {data:{user:cu}}=await supabase.auth.getUser();
    const myId=cu?.id;
    if(!myId) return;
    if(!loadRef){console.error('completeDelivery: loadRef داده نشد');return;}
    const {error}=await supabase.from('loads').update({status:'awaiting_confirmation',delivered_at:new Date().toISOString()}).eq('load_ref',loadRef).eq('requested_by',myId);
    if(error){console.error('خطا در تکمیل تحویل:',error);return;}
    const shipment=await getShipmentForLoad(loadRef);
    if(shipment){
      const {error:sigErr}=await supabase.from('shipments').update({
        sig_receiver:sigReceiver||null,
        sig_receiver_name:sigReceiverName||null,
        status:'awaiting_confirmation',
      }).eq('id',shipment.id);
      if(sigErr){console.error('خطا در ذخیره مدرک تحویل:',sigErr);}
    }
    const validDeliveryPhotos=(deliveryPhotos||[]).filter(p=>p&&p!=='uploading');
    if(validDeliveryPhotos.length>0&&shipment){
      const rows=validDeliveryPhotos.map(path=>({
        shipment_id:shipment.id,
        photo_url:path,
        document_type:'delivery_photo',
        uploaded_by:myId,
      }));
      const {error:photoErr}=await supabase.from('shipment_photos').insert(rows);
      if(photoErr){console.error('خطا در ذخیره عکس‌های تحویل:',photoErr);}
    }
    console.log('بار تحویل داده شد، در انتظار تأیید فرستنده:',loadRef);
    loadLoadsFromDb();
    refreshTripState();
  }catch(err){console.error('خطای کلی completeDelivery:',err);}
};
const getCurrentPositionAsync=()=>new Promise((resolve,reject)=>{ if(!navigator.geolocation){reject(new Error('no geo'));return;} navigator.geolocation.getCurrentPosition(resolve,reject,{enableHighAccuracy:true,timeout:10000}); });

const requestLoad=async(load)=>{
  if(ready==='loaded'){alert(lang==='de'?'Ihr Fahrzeug ist beladen. Melden Sie sich bereit, sobald es leer ist.':'Your vehicle is loaded. Report ready once it is empty.');return;}
  const MAX_PICKUP_DISTANCE_KM=150;
  if(load.pickupLat&&load.pickupLng){
    try{
      const locOk=await askLocationConsent(); if(!locOk){alert(lang==='de'?'Ohne Standortfreigabe k\u00f6nnen Sie keine Ladung anfragen.':'You cannot request a load without location access.');return;}
      const pos=await getCurrentPositionAsync();
      const dist=haversineDistance(pos.coords.latitude,pos.coords.longitude,load.pickupLat,load.pickupLng);
      if(dist>MAX_PICKUP_DISTANCE_KM){
        alert(lang==='de'?('Sie sind zu weit von der Abholadresse entfernt ('+dist+' km). Maximal erlaubt: '+MAX_PICKUP_DISTANCE_KM+' km.'):('You are too far from the pickup address ('+dist+' km). Maximum allowed: '+MAX_PICKUP_DISTANCE_KM+' km.'));
        return;
      }
    }catch(geoErr){
      console.error('خطا در گرفتن موقعیت GPS برای چک فاصله:',geoErr);
      alert(lang==='de'?'Standort konnte nicht ermittelt werden. Bitte GPS aktivieren und erneut versuchen.':'Could not determine your location. Please enable GPS and try again.');
      return;
    }
  }
  try{
    const activeLoads=await getMyActiveLoads();
    const usedWeight=activeLoads.reduce((sum,l)=>sum+(parseFloat(l.total_weight)||0),0);
    const cap=parseFloat(driverCapacity)||0;
    const thisWeight=parseFloat(load.weight)||0;

    if(cap>0&&(usedWeight+thisWeight)>cap){
      alert(`${t(lang,'capacityWarningPrefix')}\n${t(lang,'capacityWarningVehicle')} ${cap} kg\n${t(lang,'capacityWarningCurrent')} ${usedWeight} kg\n${t(lang,'capacityWarningThis')} ${thisWeight} kg\n${t(lang,'capacityWarningExceed')}`);
      return;
    }
    // چک مسیر: مقصد بار جدید باید نزدیک مقصد بارهای فعلی باشد (در امتداد مسیر)
    const ROUTE_THRESHOLD=15; // آستانه نزدیکی مقصد (بر اساس مقیاس نقشه)
    if(activeLoads.length>0){
      const offRoute=activeLoads.every(l=>cityDistance(l.to_city,load.to)>ROUTE_THRESHOLD);
      if(offRoute){
        alert(t(lang,'offRouteWarning'));
        return;
      }
    }
    const {error}=await supabase.rpc('request_load',{
      p_load_ref:load.loadRef,
      p_name:driverName||null,
      p_plate:driverPlate||null,
      p_phone:driverPhone||null,
    });
    if(error){console.error('خطا در ثبت درخواست:',error);alert('خطا: '+error.message);return;}
    console.log('درخواست ثبت شد برای:',load.loadRef);
    supabase.functions.invoke('notify-shipper-request',{body:{load_ref:load.loadRef}}).then(({error})=>{if(error)console.error('notify-shipper-request error:',error);});
    alert(t(lang,'requestSubmittedAlert'));
    loadLoadsFromDb();
    close();
  }catch(err){console.error('خطای کلی requestLoad:',err);}
};
    
// ── فرستنده درخواست راننده را قبول می‌کند ──
const acceptRequest=async(load)=>{
  try{
   const {error}=await supabase.from('loads').update({status:'assigned',assigned_at:new Date().toISOString()}).eq('load_ref',load.loadRef||load.load_ref);
    if(error){console.error('خطا در قبول درخواست:',error);alert('خطا: '+error.message);return;}
    console.log('درخواست قبول شد:',load.loadRef);
    alert(t(lang,'driverConfirmedAlert'));
    await loadLoadsFromDb();
    if(showShipperLoads) await openShipperLoads();
    close();
  }catch(err){console.error('خطای کلی acceptRequest:',err);}
};

// ── فرستنده درخواست راننده را رد می‌کند ──
const rejectRequest=async(load)=>{
  try{
    const {error}=await supabase.from('loads').update({
      status:'open',
      requested_by:null,
      requested_driver_name:null,
      requested_driver_plate:null,
      requested_driver_phone:null,
    }).eq('load_ref',load.loadRef||load.load_ref);
    if(error){console.error('خطا در رد درخواست:',error);alert('خطا: '+error.message);return;}
    console.log('درخواست رد شد:',load.loadRef);
    alert(t(lang,'requestRejectedAlert'));
    loadLoadsFromDb();
    close();
  }catch(err){console.error('خطای کلی rejectRequest:',err);}
};
// ── ذخیره shipment در دیتابیس ──
const saveShipmentToDb=async(load)=>{
  try{
    const {data,error}=await supabase.from('shipments').insert([{
      load_ref:load?.loadRef||null,
      driver_name:driverName||null,
      driver_plate:driverPlate||null,
      driver_phone:driverPhone||null,
      driver_vehicle:driverVehicle||null,
      insurance_type:load?.insured?'shipper':'euload',
      insurance_policy:insPolicyNum||null,
      insurance_company:insCompany||null,
      insurance_expiry:insExpiry||null,
      sig_sender:sigSender||null,
      sig_sender_name:sigSenderName||null,
      sig_driver:sigDriver||null,
      progress:0,
      status:'in_transit',
    }]).select();
    if(error){
      console.error('خطا در ذخیره shipment:',error);
      alert('خطا در ذخیره shipment: '+error.message);
      return null;
    }
    const shipment=data[0];
    // ── ذخیره عکس‌های بار در shipment_photos ──
    const {data:{user:cu}}=await supabase.auth.getUser();
    const validPhotos=(photos||[]).filter(p=>p&&p!=='uploading');
    if(validPhotos.length>0){
      const rows=validPhotos.map(path=>({
        shipment_id:shipment.id,
        photo_url:path,
        document_type:'cargo_photo',
        uploaded_by:cu?.id||null,
      }));
      const {error:photoErr}=await supabase.from('shipment_photos').insert(rows);
      if(photoErr){console.error('خطا در ذخیره عکس‌ها:',photoErr);}
    }
    console.log('shipment ذخیره شد:',shipment);
    return shipment;
  }catch(err){
    console.error('خطای کلی:',err);
    return null;
  }
};
// ── حذف (لغو) بار توسط فرستنده ──
const deleteLoad=async(load)=>{
  if(!window.confirm(t(lang,'confirmDeleteLoad'))) return;
  try{
    const loadRef=load.loadRef||load.load_ref;
    const {error}=await supabase.from('loads').update({status:'cancelled'}).eq('load_ref',loadRef);
    if(error){console.error('خطا در حذف بار:',error);alert('خطا در حذف: '+error.message);return;}
    // از لیست‌های محلی هم بردار
    setMyLoads(p=>p.filter(l=>l.id!==load.id));
    setDbLoads(p=>p.filter(l=>l.id!==load.id));
    setShipperAllLoads(p=>p.filter(l=>(l.load_ref||l.loadRef)!==loadRef));
    console.log('بار حذف شد:',loadRef);
    close();
    setShowShipperLoads(false);
    openShipperLoads();
  }catch(err){console.error('خطای کلی deleteLoad:',err);}
};
const ensureLoadRef=()=>{
  if(!nlGeneratedRef){
    const ref=nlGeneratedRef||genRef();
    setNlGeneratedRef(ref);
    return ref;
  }
  return nlGeneratedRef;
};

const linkPaymentToLoad=async(loadRef,loadId)=>{
  try{
    const {data:{user:cu}}=await supabase.auth.getUser();
    const {error}=await supabase.from('payments').update({
      load_id:loadId,
      shipper_auth_id:cu?.id||null,
    }).eq('load_ref',loadRef);
    if(error){console.error('خطا در لینک کردن پرداخت به بار:',error);}
  }catch(err){console.error('خطای کلی linkPaymentToLoad:',err);}
};

const submitNewLoad=async()=>{
  if(nlSubmitting) return; // جلوگیری از دابل-ساب‌میت
  setNlSubmitting(true);
  try{
    // چک تاریخ بارگیری با تاریخ سرور (ضد دستکاری)
    const today=serverDate||new Date();
    today.setHours(0,0,0,0);

    if(new Date(nlDate)<today){
      alert(t(lang,'pickupDatePastWarning'));
      return;
    }
    const ref=genRef();
    const cargo=CARGO_TYPES.find(c=>c.k===nlCargoType);
    const vehicle=VEHICLE_TYPES.find(v=>v.k===nlVehicle);
    // ── محاسبه‌ی فاصله‌ی واقعی از دقیق‌ترین مختصات موجود (آدرس خیابان > کدپستی > عدد پیش‌فرض) ──
    const pickupCoordsFinal=nlPickupCoords||nlFromSelected;
    const deliveryCoordsFinal=nlDeliveryCoords||nlToSelected;
    const calculatedDistance=(pickupCoordsFinal&&deliveryCoordsFinal)
      ?haversineDistance(pickupCoordsFinal.lat,pickupCoordsFinal.lng,deliveryCoordsFinal.lat,deliveryCoordsFinal.lng)
      :Math.floor(Math.random()*2000)+400; // در صورت نبود مختصات، مقدار پیش‌فرض
    const newLoad={
      id:Date.now(),loadRef:ref,
      shipper:companyName||'My Company',
      from:nlFrom,to:nlTo,
      price:parseInt(nlPrice)||0,
      weight:nlPkgType==='pallet'?(parseInt(nlPallets)||0)*(parseInt(nlPalletWeight)||0):parseInt(nlWeight)||0,
      type:vehicle?vehicle.name:'Curtainside',
      distance:calculatedDistance,
      depart:nlDate+' '+nlTime,arrive:'TBD',
      insured:nlInsured,cargoLabel:cargo?cargo.name:'General Goods',
      pkgType:nlPkgType,pallets:nlPallets,palletWeight:nlPalletWeight,
      length:nlLength,width:nlWidth,height:nlHeight,
      notes:nlNotes,receiverName:nlReceiverName,isMine:true,
      initialStatus:nlPayerType==='sender'?'open':'awaiting_payer_payment',
    };
    const savedLoad=await saveLoadToDb(newLoad);
    if(savedLoad&&nlPayerType==='sender'){
      await linkPaymentToLoad(ref,savedLoad.id);
    }
    if(savedLoad&&nlPayerType!=='sender'){
      try{
        const emailRes=await fetch('https://oqnrxrowiewwrtroegwl.supabase.co/functions/v1/send-payment-email',{
          method:'POST',
          headers:{
            'apikey':'sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
            'Authorization':'Bearer sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
            'Content-Type':'application/json',
          },
          body:JSON.stringify({ load_ref:ref }),
        });
        const emailData=await emailRes.json();
        if(emailData.error){console.error('خطا در ارسال ایمیل پرداخت:',emailData.error);}
      }catch(emailErr){console.error('خطای کلی ارسال ایمیل پرداخت:',emailErr);}
    }
    await loadLoadsFromDb();
    resetNewLoad();close();
  } finally {
    setNlSubmitting(false);
  }
};


const allLoadsRaw=[...myLoads,...dbLoads];
const allLoads=allLoadsRaw.filter(l=>{
  // بار تأییدشده فقط برای راننده تاییدشده و شرکت صاحبش دیده شود
  if(l.status==='assigned'&&!l.isMine&&l.requested_by!==authUser?.id) return false;
  // بار در انتظار پرداخت Payer فقط برای فرستنده‌ی خودش دیده شود
  if(l.status==='awaiting_payer_payment'&&!l.isMine) return false;
  return true;
});
  const onLoad=load=>{
    setSelLoad(load);
    setLoadShipment(null);
    if(load.isMine&&load.loadRef){
      getShipmentForLoad(load.loadRef).then(s=>setLoadShipment(s));
    }
    if(userStep===STEP.GUEST){setModal('gate');return;}
    if(userStep===STEP.LOGGED_IN){setModal('payment');return;}
    if(userStep===STEP.PAID){setModal('docs');return;}
    if(userStep===STEP.DOCS_SENT){setModal('pending');return;}
    if(userRole==='shipper'){setModal('detail');return;}
    setModal('detail');
  };

  const selectedVehicle=VEHICLE_TYPES.find(v=>v.k===driverVehicle);
  // ── دکمه ثبت‌نام (Register) برای GUEST ──
const RegisterBtn=()=>{
  if(userStep!==STEP.GUEST) return null;
  return(
    <button onClick={()=>{setScreen('user_type');setModal('gate');}} style={{padding:'6px 14px',background:'#e8f01a',color:'#0a0c0f',border:'1px solid #e8f01a',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer',letterSpacing:0.5,textTransform:'uppercase',marginRight:8}}>{t(lang,'register')}</button>
  );
};
// ── دکمه ورود (Sign In) برای GUEST ──
const SignInBtn=()=>{
  if(userStep!==STEP.GUEST) return null;
  return(
    <button onClick={()=>setShowLogin(true)} style={{padding:'6px 14px',background:'rgba(232,240,26,0.1)',color:'#e8f01a',border:'1px solid rgba(232,240,26,0.4)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer',letterSpacing:0.5,textTransform:'uppercase',marginRight:8}}>{t(lang,'signIn')}</button>
  );
};

// ── دکمه خروج (Logout) برای کاربر login شده ──
const ShipperLoadsBtn=()=>{
  if(userStep!==STEP.APPROVED) return null;
  if(userRole!=='shipper') return null;
  return(
    <button onClick={openShipperLoads} title={t(lang,'myLoadsTitle')} style={{padding:'6px 11px',background:'rgba(136,146,164,0.1)',color:'#cbd5e0',border:'1px solid rgba(136,146,164,0.4)',borderRadius:8,fontSize:13,cursor:'pointer',marginRight:8}}>📦</button>
  );
};
const MyLoadsBtn=()=>{
  if(userStep!==STEP.APPROVED) return null;
  if(userRole==='shipper') return null;
  return(
    <button onClick={openMyLoads} title={t(lang,'myLoadsTitle')} style={{padding:'6px 11px',background:'rgba(136,146,164,0.1)',color:'#cbd5e0',border:'1px solid rgba(136,146,164,0.4)',borderRadius:8,fontSize:13,cursor:'pointer',marginRight:8}}>📦</button>
  );
};
const ProfileBtn=()=>{
  if(userStep===STEP.GUEST) return null;
  const missingDocs=(userRole==='driver'||userRole==='carrier'||userRole==='shipper')?4-docs.length:0;
  return(
    <button onClick={()=>setShowProfile(true)} title={t(lang,'profileTitle')} style={{position:'relative',padding:'6px 11px',background:missingDocs>0?'rgba(232,240,26,0.1)':'rgba(136,146,164,0.1)',color:missingDocs>0?'#e8f01a':'#cbd5e0',border:missingDocs>0?'1px solid rgba(232,240,26,0.4)':'1px solid rgba(136,146,164,0.4)',borderRadius:8,fontSize:13,cursor:'pointer',marginRight:8}}>
      👤
      {missingDocs>0&&(
        <span style={{position:'absolute',top:-9,right:-9,background:'#ef4444',color:'#fff',fontSize:15,fontWeight:900,minWidth:24,height:24,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',border:'2px solid #12161c',padding:'0 4px'}}>{missingDocs}</span>
      )}
    </button>
  );
};
const LogoutBtn=()=>{
  if(userStep===STEP.GUEST) return null;
 
  return(
    <button onClick={async()=>{
      if(window.confirm(t(lang,'signOutConfirm'))){
        await authSignOut();
      }
    }} style={{padding:'6px 10px',background:'rgba(252,129,129,0.1)',color:'#fc8181',border:'1px solid rgba(252,129,129,0.4)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer',letterSpacing:0.5,textTransform:'uppercase',marginRight:8,marginLeft:10}}>{t(lang,'logout')}</button>
  );
};
const Pill=()=>{
    if(userStep===STEP.GUEST) return null;
    if(userStep===STEP.LOGGED_IN) return <span className="pill pa"><span className="pdot" style={{background:'#f59e0b'}}/>Pay to Access</span>;
    if(userStep===STEP.PAID) return <span className="pill pa"><span className="pdot" style={{background:'#f59e0b'}}/>Upload Docs</span>;
    if(userStep===STEP.DOCS_SENT) return <span className="pill pa" onClick={()=>docs.length===0&&setModal('docs')} style={{cursor:docs.length===0?'pointer':'default'}}><span className="pdot" style={{background:'#f59e0b'}}/>{docs.length===0?'Upload Docs':'Under Review'}</span>;
    if(userRole==='shipper') return <span className="pill pg"><span className="pdot" style={{background:'#22c55e'}}/>Shipper</span>;
    return ready==='free'
      ?<span className={'pill pg'} onClick={()=>setModal('status')}><span className={'pdot'} style={{background:'#22c55e'}}/>{lang==='de'?'Frei':'Free'}</span>
      :<span className={'pill pr2'} onClick={()=>setModal('status')}><span className={'pdot'} style={{background:'#ef4444'}}/>{lang==='de'?'Beladen':'Loaded'}</span>;
  };

  const StatusBtn=()=>{
    if(userStep!==STEP.APPROVED) return null;
    if(userRole==='shipper') return null;
    return ready==='free'
      ?<button className={'status-btn'} onClick={()=>setModal('status')} style={{background:'rgba(34,197,94,0.1)',color:'#86efac',border:'1px solid rgba(34,197,94,0.3)'}}>{'\uD83D\uDFE2 '}{lang==='de'?'Frei':'Free'}</button>
      :<button className={'status-btn'} onClick={()=>{if(window.confirm(lang==='de'?'Fahrzeug leer \u2013 bereit f\u00fcr neue Ladung?':'Vehicle empty \u2013 ready for a new load?'))setReadyState('free');}} style={{background:'rgba(239,68,68,0.1)',color:'#fc8181',border:'1px solid rgba(239,68,68,0.3)'}}>{'\uD83D\uDD34 '}{lang==='de'?'Beladen \u2013 bereit melden':'Loaded \u2013 report ready'}</button>;
  };
const TrackBtn=()=>{
  if(userStep!==STEP.APPROVED||userRole!=='shipper'||trackedLoads.length===0) return null;
  return(
    <button onClick={()=>{setSelectedTrackLoad(trackedLoads[0]);setModal('tracking');}}
      style={{borderRadius:10,padding:'5px 12px',fontSize:10,fontWeight:700,cursor:'pointer',
        letterSpacing:0.5,fontFamily:'Barlow Condensed',position:'relative',
        background:'rgba(59,130,246,0.15)',color:'#93c5fd',border:'1px solid rgba(59,130,246,0.3)'}}>
      📍 {t(lang,'trackingBtn')}
      <span style={{position:'absolute',top:-5,right:-5,background:'#22c55e',color:'#0a0c0f',
        fontSize:9,fontWeight:900,width:16,height:16,borderRadius:'50%',
        display:'flex',alignItems:'center',justifyContent:'center'}}>{trackedLoads.length}</span>
    </button>
  );
};

const DemoTrackBtn=()=>{
  if(userStep!==STEP.APPROVED||userRole!=='shipper') return null;
  return(
    <button onClick={()=>{
      const demoLoad={id:Date.now(),loadRef:genRef(),from:'Berlin',to:'Paris',distance:1050,
        driverName:'Hans Schmidt',driverPlate:'B-AB 1234',driverPhone:'+49 170 555 0123',driverVehicle:'curtainside'};
      setTrackedLoads(p=>[...p,demoLoad]);
      setTrackingProgress(p=>({...p,[demoLoad.id]:0.15}));
    }} style={{borderRadius:10,padding:'5px 12px',fontSize:10,fontWeight:700,cursor:'pointer',
      letterSpacing:0.5,fontFamily:'Barlow Condensed',
      background:'rgba(168,85,247,0.15)',color:'#c4b5fd',border:'1px solid rgba(168,85,247,0.3)'}}>
      🧪 {t(lang,'testTracking')}
    </button>
  );
};
  const NewLoadBtn=()=>{
    // مرحله پرداخت
    if(userStep===STEP.LOGGED_IN) return(<button className="new-load-btn" onClick={()=>setModal('payment')}>{t(lang,'continuePayment')}</button>);
    // مرحله ارسال مدارک
    if(userStep===STEP.PAID) return(<button className="new-load-btn" onClick={()=>setModal('docs')}>{t(lang,'continueDocs')}</button>);
    // در حال بررسی
    if(userStep===STEP.DOCS_SENT) return(<button className="new-load-btn" onClick={()=>setModal('pending')}>{t(lang,'underReview')} ⏳</button>);
    // تایید شده + shipper → ثبت بار
    if(userStep===STEP.APPROVED&&userRole==='shipper') return(<button className="new-load-btn" onClick={()=>{resetNewLoad();setModal('new_load');}}>{t(lang,'newLoadBtn')}</button>);
    return null;
  };

  const renderModal=()=>{
    if(showShipperLoads){
      const twoYearsAgo=new Date();twoYearsAgo.setFullYear(twoYearsAgo.getFullYear()-2);
      const myShipperLoads=shipperAllLoads.filter(l=>l.created_at&&new Date(l.created_at)>=twoYearsAgo);
      
      const statusInfo=(s)=>{
        if(s==='open') return {t:t(lang,'statusWaitingDriver'),c:'#93c5fd',bg:'rgba(59,130,246,0.1)',br:'rgba(59,130,246,0.3)'};
        if(s==='pending') return {t:'⏳ '+t(lang,'statusPendingDriver'),c:'#fcd34d',bg:'rgba(245,158,11,0.1)',br:'rgba(245,158,11,0.3)'};
        if(s==='assigned') return {t:'🚛 '+t(lang,'statusInTransit'),c:'#86efac',bg:'rgba(34,197,94,0.1)',br:'rgba(34,197,94,0.3)'};
        if(s==='awaiting_confirmation') return {t:'📋 '+(lang==='de'?'Prüfung erforderlich':'Review needed'),c:'#fcd34d',bg:'rgba(245,158,11,0.15)',br:'rgba(245,158,11,0.4)'};
        if(s==='delivered') return {t:'✓ '+t(lang,'statusDelivered'),c:'#a0aec0',bg:'rgba(136,146,164,0.1)',br:'rgba(136,146,164,0.3)'};
        return {t:s,c:'#8892a4',bg:'rgba(136,146,164,0.1)',br:'rgba(136,146,164,0.3)'};
      };
      return(<Overlay onClose={()=>setShowShipperLoads(false)}>
        <div className="ttl">📦 {t(lang,'myLoadsModalTitle')}</div>
        <div className="sub">{t(lang,'shipperLoadsSubtitle')}</div>
        {myShipperLoads.length===0?(
          <div style={{background:'rgba(136,146,164,0.05)',border:'1px solid #232b36',borderRadius:10,padding:20,fontSize:13,color:'#8892a4',textAlign:'center',marginBottom:10}}>{t(lang,'noLoadsShipper')}</div>
        ):(
          myShipperLoads.map(l=>{
            const si=statusInfo(l.status);
            return(
              <div key={l.load_ref} style={{background:'#181d25',border:'1px solid #232b36',borderRadius:10,padding:12,marginBottom:8}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
                  <div style={{fontSize:14,fontWeight:700,color:'#93c5fd'}}>{l.from_city} → {l.to_city}</div>
                  <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:5,background:si.bg,color:si.c,border:'1px solid '+si.br}}>{si.t}</span>
                </div>
                <div style={{fontSize:11,color:'#8892a4',marginBottom:3}}>{t(lang,'weightLbl')}: {l.total_weight} kg · {t(lang,'priceLbl')}: {l.price} EUR</div>
                <div style={{fontSize:10,color:'#4a5568',fontFamily:'Barlow Condensed',letterSpacing:0.5}}>{l.load_ref} · {l.created_at?new Date(l.created_at).toLocaleDateString('de-DE'):'—'}</div>
                {l.requested_driver_name&&(l.status==='pending'||l.status==='assigned'||l.status==='delivered')&&(
                  <div style={{fontSize:11,color:'#93c5fd',marginTop:5,borderTop:'1px solid #232b36',paddingTop:5}}>{t(lang,'driverLbl')}: {l.requested_driver_name} — {t(lang,'plateLbl')} {l.requested_driver_plate}</div>
                )}
                {l.status==='assigned'&&(
                  <>
                  <button onClick={()=>{fetchRealTrackingLocation(l.load_ref);setShowShipperLoads(false);setModal('real_tracking');}} style={{width:'100%',marginTop:8,padding:'7px',background:'rgba(59,130,246,0.1)',color:'#93c5fd',border:'1px solid rgba(59,130,246,0.3)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>📍 {t(lang,'trackingBtn')}</button>
                  <button onClick={()=>openShipmentDoc(l)} style={{width:'100%',marginTop:6,padding:'7px',background:'rgba(136,146,164,0.1)',color:'#cbd5e0',border:'1px solid rgba(136,146,164,0.3)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>📄 {lang==='de'?'Frachtdokument ansehen':'View shipment document'}</button>
                  </>
                )}
                {l.status==='pending'&&(
                  <div style={{display:'flex',gap:6,marginTop:8}}>
                    <button onClick={()=>acceptRequest(l)} style={{flex:1,padding:'7px',background:'rgba(34,197,94,0.1)',color:'#86efac',border:'1px solid rgba(34,197,94,0.3)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>✓ {t(lang,'approveBtn')}</button>
                    <button onClick={()=>rejectRequest(l)} style={{flex:1,padding:'7px',background:'rgba(239,68,68,0.08)',color:'#fc8181',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>✕ {t(lang,'rejectBtn')}</button>
                  </div>
                )}
                {l.status==='open'&&(
                  <button onClick={()=>deleteLoad(l)} style={{width:'100%',marginTop:8,padding:'7px',background:'rgba(239,68,68,0.08)',color:'#fc8181',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>🗑 {t(lang,'deleteLoadBtn')}</button>
                )}
                {l.status==='awaiting_confirmation'&&(
                  <button onClick={()=>openDeliveryConfirm(l)} style={{width:'100%',marginTop:8,padding:'8px',background:'rgba(245,158,11,0.15)',color:'#fcd34d',border:'1px solid rgba(245,158,11,0.4)',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>📋 {lang==='de'?'Zustand prüfen & bestätigen':'Review & confirm condition'}</button>
                )}
                {l.status==='delivered'&&(
                  <button onClick={()=>openDeliveryProof(l)} style={{width:'100%',marginTop:8,padding:'7px',background:'rgba(34,197,94,0.1)',color:'#86efac',border:'1px solid rgba(34,197,94,0.3)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>📋 {lang==='de'?'Lieferbeleg ansehen':'View Delivery Proof'}</button>
                )}
              </div>
            );
          })
        )}
        <button className="btn bo" onClick={()=>setShowShipperLoads(false)}>{t(lang,'close')}</button>
      </Overlay>);
    }
    if(showShipmentDoc&&shipmentDocData){
      const d=shipmentDocData;
      const statusLabel=d.loadStatus==='assigned'?(lang==='de'?'Unterwegs':'In transit'):d.loadStatus==='awaiting_confirmation'?(lang==='de'?'Geliefert – Prüfung ausstehend':'Delivered – review pending'):d.loadStatus==='delivered'?(lang==='de'?'Abgeschlossen':'Completed'):d.loadStatus;
      return(<Overlay onClose={()=>{setShowShipmentDoc(false);setShipmentDocData(null);}}>
        <div className="ttl">📄 {lang==='de'?'Frachtdokument':'Shipment Document'}</div>
        <div className="sub">{d.from} → {d.to}</div>

        <div className="pb">
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Status':'Status'}</span><span style={{fontSize:12,fontWeight:700,color:'#e8f01a'}}>{statusLabel}</span></div>
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Gewicht':'Weight'}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{d.weight||'—'} kg</span></div>
          <div className="prow"><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Frachtart':'Cargo type'}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{d.cargoType||'—'}</span></div>
        </div>

        <div className="sec-lbl">{lang==='de'?'Fahrzeug & Fahrer':'Vehicle & Driver'}</div>
        <div className="pb">
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>👤 {lang==='de'?'Fahrer':'Driver'}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{d.driver_name||'—'}</span></div>
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>🔢 {lang==='de'?'Kennzeichen':'Plate'}</span><span style={{fontSize:12,fontWeight:700,fontFamily:'Barlow Condensed',letterSpacing:1,color:'#e8f01a'}}>{d.driver_plate||'—'}</span></div>
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>🚚 {lang==='de'?'Fahrzeugtyp':'Vehicle type'}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{d.driver_vehicle||'—'}</span></div>
          <div className="prow"><span style={{fontSize:11,color:T.soft}}>📞 {lang==='de'?'Telefon':'Phone'}</span><span style={{fontSize:12,fontWeight:600,color:'#93c5fd'}}>{d.driver_phone||'—'}</span></div>
        </div>

        {d.sig_sender&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Unterschrift Absender (Abholung)':'Sender Signature (Pickup)'}</div>
            <img src={d.sig_sender} alt="sender signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
            {d.sig_sender_name&&<div style={{fontSize:11,color:T.soft,marginTop:4}}>{d.sig_sender_name}</div>}
          </div>
        )}
        {d.sig_driver&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Unterschrift Fahrer (Abholung)':'Driver Signature (Pickup)'}</div>
            <img src={d.sig_driver} alt="driver signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
          </div>
        )}
        {d.cargoPhotoUrls&&d.cargoPhotoUrls.length>0&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Fotos bei Abholung':'Pickup Photos'}</div>
            <div className="photo-grid">
              {d.cargoPhotoUrls.map((url,idx)=>(
                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={'cargo-photo'+idx} style={{width:'100%',height:80,objectFit:'cover',borderRadius:10,border:'1px solid #232b36'}}/>
                </a>
              ))}
            </div>
          </div>
        )}

        {d.sig_receiver&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Unterschrift Empfänger (Lieferung)':'Receiver Signature (Delivery)'}</div>
            <img src={d.sig_receiver} alt="receiver signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
          </div>
        )}
        {d.deliveryPhotoUrls&&d.deliveryPhotoUrls.length>0&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Fotos bei Lieferung':'Delivery Photos'}</div>
            <div className="photo-grid">
              {d.deliveryPhotoUrls.map((url,idx)=>(
                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={'delivery-photo'+idx} style={{width:'100%',height:80,objectFit:'cover',borderRadius:10,border:'1px solid #232b36'}}/>
                </a>
              ))}
            </div>
          </div>
        )}

        <button className="btn bo" onClick={()=>{setShowShipmentDoc(false);setShipmentDocData(null);}}>{t(lang,'close')}</button>
      </Overlay>);
    }
    if(showDeliveryConfirm&&deliveryConfirmData){
      const d=deliveryConfirmData;
      return(<Overlay onClose={()=>{setShowDeliveryConfirm(false);setDeliveryConfirmData(null);}}>
        <div className="ttl">📋 {lang==='de'?'Zustand der Ladung prüfen':'Review cargo condition'}</div>
        <div className="sub">{d.from} → {d.to}</div>

        <div className="sec-lbl">{lang==='de'?'Fahrzeug & Fahrer':'Vehicle & Driver'}</div>
        <div className="pb">
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>👤 {lang==='de'?'Fahrer':'Driver'}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{d.driver_name||'—'}</span></div>
          <div className="prow"><span style={{fontSize:11,color:T.soft}}>🔢 {lang==='de'?'Kennzeichen':'Plate'}</span><span style={{fontSize:12,fontWeight:700,fontFamily:'Barlow Condensed',letterSpacing:1,color:'#e8f01a'}}>{d.driver_plate||'—'}</span></div>
        </div>

        {d.sig_receiver&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Unterschrift Empfänger':'Receiver Signature'}</div>
            <img src={d.sig_receiver} alt="receiver signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
            {d.sig_receiver_name&&<div style={{fontSize:11,color:T.soft,marginTop:4}}>{d.sig_receiver_name}</div>}
          </div>
        )}

        {d.deliveryPhotoUrls&&d.deliveryPhotoUrls.length>0&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Fotos bei Lieferung':'Delivery Photos'}</div>
            <div className="photo-grid">
              {d.deliveryPhotoUrls.map((url,idx)=>(
                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={'delivery-photo'+idx} style={{width:'100%',height:80,objectFit:'cover',borderRadius:10,border:'1px solid #232b36'}}/>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="sec-lbl">{lang==='de'?'Ist die Ladung unbeschädigt angekommen?':'Did the cargo arrive undamaged?'}</div>
        <div style={{fontSize:11,color:T.soft,marginBottom:10}}>{lang==='de'?'Die Zahlung an den Fahrer wird in beiden Fällen freigegeben.':'Payment to the driver will be released in either case.'}</div>
        <textarea className="inp" rows={2} placeholder={lang==='de'?'Optional: Schadensbeschreibung':'Optional: damage description'} value={confirmDamageNote} onChange={e=>setConfirmDamageNote(e.target.value)} style={{resize:'none',height:'60px'}}/>
        <button className="btn bs" onClick={async()=>{await confirmDeliveryHealth({load_ref:d.loadRefRaw},true,null);setShowDeliveryConfirm(false);setDeliveryConfirmData(null);}}>✓ {lang==='de'?'Unbeschädigt bestätigen':'Confirm intact'}</button>
        <button className="btn bdan" onClick={async()=>{await confirmDeliveryHealth({load_ref:d.loadRefRaw},false,confirmDamageNote);setShowDeliveryConfirm(false);setDeliveryConfirmData(null);}}>⚠ {lang==='de'?'Schaden melden':'Report damage'}</button>
        <button className="btn bo" onClick={()=>{setShowDeliveryConfirm(false);setDeliveryConfirmData(null);}}>{t(lang,'close')}</button>
      </Overlay>);
    }
    if(showDeliveryProof&&deliveryProofData){
      const d=deliveryProofData;
      return(<Overlay onClose={()=>{setShowDeliveryProof(false);setDeliveryProofData(null);}}>
        <div className="ttl">📋 {lang==='de'?'Lieferbeleg':'Delivery Proof'}</div>
        <div className="sub">{d.from} → {d.to}</div>

        <div className="sec-lbl">{lang==='de'?'Fahrzeug & Fahrer':'Vehicle & Driver'}</div>
        <div className="pb">
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>👤 {lang==='de'?'Fahrer':'Driver'}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{d.driver_name||'—'}</span></div>
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>🔢 {lang==='de'?'Kennzeichen':'Plate'}</span><span style={{fontSize:12,fontWeight:700,fontFamily:'Barlow Condensed',letterSpacing:1,color:'#e8f01a'}}>{d.driver_plate||'—'}</span></div>
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>🚚 {lang==='de'?'Fahrzeugtyp':'Vehicle Type'}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{d.driver_vehicle||'—'}</span></div>
          <div className="prow"><span style={{fontSize:11,color:T.soft}}>📞 {lang==='de'?'Telefon':'Phone'}</span><span style={{fontSize:12,fontWeight:600,color:'#93c5fd'}}>{d.driver_phone||'—'}</span></div>
        </div>

        <div className="sec-lbl">{lang==='de'?'Status der Lieferung':'Delivery Status'}</div>
        <div className="pb">
          <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Status':'Status'}</span><span style={{fontSize:12,fontWeight:600,color:d.delivery_ok?'#86efac':'#fc8181'}}>{d.delivery_ok?'✓ '+(lang==='de'?'Unbeschädigt geliefert':'Delivered Intact'):'⚠ '+(lang==='de'?'Mit Schaden':'With Damage')}</span></div>
          <div className="prow"><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Empfänger':'Receiver'}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{d.sig_receiver_name||'—'}</span></div>
        </div>
        {!d.delivery_ok&&d.damage_note&&<div className="warn-box">{d.damage_note}</div>}

        {d.sig_sender&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Unterschrift Absender (Abholung)':'Sender Signature (Pickup)'}</div>
            <img src={d.sig_sender} alt="sender signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
            {d.sig_sender_name&&<div style={{fontSize:11,color:T.soft,marginTop:4}}>{d.sig_sender_name}</div>}
          </div>
        )}
        {d.sig_driver&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Unterschrift Fahrer (Abholung)':'Driver Signature (Pickup)'}</div>
            <img src={d.sig_driver} alt="driver signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
          </div>
        )}
        {d.cargoPhotoUrls&&d.cargoPhotoUrls.length>0&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Fotos bei Abholung':'Pickup Photos'}</div>
            <div className="photo-grid">
              {d.cargoPhotoUrls.map((url,idx)=>(
                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={'cargo-photo'+idx} style={{width:'100%',height:80,objectFit:'cover',borderRadius:10,border:'1px solid #232b36'}}/>
                </a>
              ))}
            </div>
          </div>
        )}

        {d.sig_receiver&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Unterschrift Empfänger (Lieferung)':'Receiver Signature (Delivery)'}</div>
            <img src={d.sig_receiver} alt="receiver signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
          </div>
        )}
        {d.deliveryPhotoUrls&&d.deliveryPhotoUrls.length>0&&(
          <div style={{marginBottom:13}}>
            <div className="sec-lbl">{lang==='de'?'Fotos bei Lieferung':'Delivery Photos'}</div>
            <div className="photo-grid">
              {d.deliveryPhotoUrls.map((url,idx)=>(
                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={'delivery-photo'+idx} style={{width:'100%',height:80,objectFit:'cover',borderRadius:10,border:'1px solid #232b36'}}/>
                </a>
              ))}
            </div>
          </div>
        )}

        <button className="btn bo" onClick={()=>{setShowDeliveryProof(false);setDeliveryProofData(null);}}>{t(lang,'close')}</button>
      </Overlay>);
    }
    if(showMyLoads) return(<Overlay onClose={()=>setShowMyLoads(false)}>
      <div className="ttl">📦 {t(lang,'myLoadsModalTitle')}</div>
      <div className="sub">{t(lang,'myActiveLoadsSubtitle')}</div>
      {myActiveLoads.length===0?(
        <div style={{background:'rgba(136,146,164,0.05)',border:'1px solid #232b36',borderRadius:10,padding:20,fontSize:13,color:'#8892a4',textAlign:'center',marginBottom:10}}>{t(lang,'noActiveLoads')}</div>
      ):(
        myActiveLoads.map((l,i)=>(
          <div key={l.load_ref} style={{background:'#181d25',border:'1px solid #232b36',borderRadius:10,padding:12,marginBottom:8,position:'relative'}}>
            <div style={{position:'absolute',top:12,left:12,width:26,height:26,background:'#e8f01a',color:'#0a0c0f',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:900,fontSize:13,fontFamily:'Barlow Condensed'}}>{i+1}</div>
            <div style={{paddingRight:0,paddingLeft:34}}>
              <div style={{fontSize:14,fontWeight:700,marginBottom:4,color:'#93c5fd'}}>{l.from_city} → {l.to_city}</div>
              <div style={{fontSize:11,color:'#8892a4',marginBottom:6}}>{t(lang,'weightLbl')}: {l.total_weight} kg · {l.cargo_type||t(lang,'generalCargo')}</div>
              <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:5,background:l.status==='assigned'?'rgba(34,197,94,0.1)':'rgba(245,158,11,0.1)',color:l.status==='assigned'?'#86efac':'#fcd34d',border:'1px solid '+(l.status==='assigned'?'rgba(34,197,94,0.3)':'rgba(245,158,11,0.3)')}}>{l.status==='assigned'?'✓ '+t(lang,'statusConfirmed'):'⏳ '+t(lang,'statusWaitingApproval')}</span>{(()=>{const showDelivery=l.status==='assigned';const addr=showDelivery?l.delivery_address:l.pickup_address;const addrLat=showDelivery?l.delivery_lat:l.pickup_lat;const addrLng=showDelivery?l.delivery_lng:l.pickup_lng;const addrLabel=showDelivery?(lang==='de'?'Lieferadresse':'Delivery address'):(lang==='de'?'Abholadresse':'Pickup address');return addr&&<div style={{marginTop:8,marginBottom:2}}><div style={{fontSize:11,color:'#8892a4'}}>{addrLabel}: <span style={{color:'#e2e8f0',fontWeight:600}}>{addr}</span></div><a href={addrLat&&addrLng?'https://www.google.com/maps?q='+addrLat+','+addrLng:'https://www.google.com/maps/search/?api=1&query='+encodeURIComponent(addr)} target='_blank' rel='noopener noreferrer' style={{fontSize:11,color:'#93c5fd',textDecoration:'underline'}}>🗺 {lang==='de'?'In Google Maps oeffnen':'Open in Google Maps'}</a></div>;})()}
              {l.status==='assigned'?(
                <>
                <button onClick={()=>openShipmentDoc(l)} style={{marginTop:8,width:'100%',padding:'7px',background:'rgba(59,130,246,0.1)',color:'#93c5fd',border:'1px solid rgba(59,130,246,0.3)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>📄 {lang==='de'?'Frachtdokument ansehen':'View shipment document'}</button><button onClick={()=>{setShowMyLoads(false);setPoliceQRLoad(l);}} style={{marginTop:8,width:'100%',padding:'7px',background:'rgba(232,240,26,0.08)',color:'#e8f01a',border:'1px solid rgba(232,240,26,0.35)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>🚓 {lang==='de'?'Fuer Polizeikontrolle zeigen':'Show for police check'}</button>
                <button onClick={async()=>{
  const loadObj={...l,loadRef:l.load_ref,from:l.from_city,to:l.to_city,receiverName:l.receiver_name,pickupLat:l.pickup_lat,pickupLng:l.pickup_lng,deliveryLat:l.delivery_lat,deliveryLng:l.delivery_lng};
  const shipment=await getShipmentForLoad(l.load_ref);
  setShowMyLoads(false);
  if(!shipment){
    setSelLoad(loadObj);
    resetAccept();
    setModal('accept_load');
  }else{
    setActiveLoad(loadObj);
    resetDelivery();
    setModal('delivery');
  }
}} style={{marginTop:8,width:'100%',padding:'8px',background:'rgba(34,197,94,0.1)',color:'#86efac',border:'1px solid rgba(34,197,94,0.4)',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>📦 {t(lang,'deliverThisLoad')}</button>
                </>
              ):(
                <button onClick={()=>cancelMyRequest(l.load_ref)} style={{marginTop:8,width:'100%',padding:'7px',background:'rgba(239,68,68,0.08)',color:'#fc8181',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>✕ {t(lang,'cancelThisRequest')}</button>
              )}
            </div>
          </div>
        ))
      )}
      {myDeliveredLoads.length>0&&(
        <div style={{marginTop:16,borderTop:'1px solid #232b36',paddingTop:14}}>
          <div className="sec-lbl">📜 {t(lang,'deliveryHistory')}</div>
          {myDeliveredLoads.map(l=>(
            <div key={l.load_ref} style={{background:'rgba(136,146,164,0.05)',border:'1px solid #232b36',borderRadius:10,padding:10,marginBottom:7}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div style={{fontSize:13,fontWeight:600,color:'#93c5fd'}}>{l.from_city} → {l.to_city}</div>
                <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:5,background:l.status==='delivered'?'rgba(34,197,94,0.1)':'rgba(245,158,11,0.1)',color:l.status==='delivered'?'#86efac':'#fcd34d',border:'1px solid '+(l.status==='delivered'?'rgba(34,197,94,0.3)':'rgba(245,158,11,0.3)')}}>{l.status==='delivered'?'✓ '+t(lang,'delivered'):(lang==='de'?'⏳ Prüfung ausstehend':'⏳ Review pending')}</span>
              </div>
              <div style={{fontSize:10,color:'#8892a4',marginTop:4}}>{t(lang,'weightLbl')}: {l.total_weight} kg · {l.delivered_at?new Date(l.delivered_at).toLocaleDateString('de-DE'):'—'}</div>
            </div>
          ))}
        </div>
      )}
      <button className="btn bo" onClick={()=>setShowMyLoads(false)}>{t(lang,'close')}</button>
    </Overlay>);
    if(policeQRLoad) return(<Overlay onClose={()=>setPoliceQRLoad(null)}>
      <div className="ttl">🚓 {lang==='de'?'Polizeikontrolle':'Police check'}</div>
      <div className="sub">{lang==='de'?'Der Beamte kann diesen Code scannen, um die Frachtdaten zu ueberpruefen.':'The officer can scan this code to verify the shipment details.'}</div>
      <div style={{background:'#fff',padding:20,borderRadius:12,display:'flex',justifyContent:'center',marginBottom:14}}>
        <QRCodeSVG value={'https://euload.imperialnexus.de/?verify='+policeQRLoad.verify_token} size={220}/>
      </div>
      <div style={{fontSize:12,color:'#8892a4',textAlign:'center',marginBottom:14}}>{policeQRLoad.load_ref}</div>
      <button className="btn bo" onClick={()=>setPoliceQRLoad(null)}>{t(lang,'close')}</button>
    </Overlay>);
    if(showProfile) return(<Overlay onClose={()=>{setShowProfile(false);setNewPassword('');setNewPassword2('');}}>
      <div className="ttl" style={{color:'#93c5fd'}}>👤 {t(lang,'profileModalTitle')}</div>
      <div className="sub">{t(lang,'profileSubtitle')}</div>
      <div className="pb" style={{marginBottom:14}}>
        {[[lang==='de'?'Kundennummer':'Customer number',customerNumber||'—'],[t(lang,'roleLbl'),userRole==='shipper'?t(lang,'roleShipper'):userRole==='carrier'?t(lang,'roleCarrier'):t(lang,'roleDriver')],[t(lang,'nameLblProfile'),(userRole==='shipper'||userRole==='carrier')?(companyName||'—'):(driverName||'—')],[t(lang,'emailPhoneLbl'),authUser?.email||'—']].map(([k,v])=>(<div key={k} className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:'#93c5fd'}}>{k}</span><span style={{fontSize:12,fontWeight:600,color:'#93c5fd'}}>{v}</span></div>))}
        {userRole!=='shipper'&&userRole!=='carrier'&&[[t(lang,'plateLbl'),driverPlate||'—'],[t(lang,'capacityLbl'),driverCapacity?driverCapacity+' kg':'—']].map(([k,v])=>(<div key={k} className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:'#93c5fd'}}>{k}</span><span style={{fontSize:12,fontWeight:600,color:'#93c5fd'}}>{v}</span></div>))}
      </div>
      {(userRole==='shipper'||userRole==='carrier')&&(
        <div style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.25)',borderRadius:10,padding:12,marginBottom:14,fontSize:11.5,color:'#93c5fd',lineHeight:1.7}}>
          ℹ️ {t(lang,'companyInfoNote')}
        </div>
      )}
      {(userRole==='driver'||userRole==='carrier'||userRole==='shipper')&&(
        <>
          <div className="sec-lbl">{lang==='de'?'Dokumente':'Documents'}</div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#181d25',border:'1px solid #232b36',borderRadius:10,padding:'10px 13px',marginBottom:14}}>
            <span style={{fontSize:12,color:T.soft}}>{docs.length}/4 {lang==='de'?'hochgeladen':'uploaded'}</span>
            <button type="button" onClick={()=>{setShowProfile(false);setModal('docs');}} style={{padding:'5px 12px',background:'rgba(232,240,26,0.1)',color:'#e8f01a',border:'1px solid rgba(232,240,26,0.3)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>{lang==='de'?'Verwalten':'Manage'}</button>
          </div>
        </>
      )}
      {userRole==='carrier'&&(
        <>
          <div className="sec-lbl">{lang==='de'?'Fuhrpark verwalten':'Manage fleet'}</div>
          <div style={{fontSize:11,color:T.soft,marginBottom:10}}>{lang==='de'?'Jedes Fahrzeug kostet 39 EUR/Monat.':'Each vehicle costs 39 EUR/month.'}</div>
          <div style={{display:'flex',gap:8,marginBottom:10}}>
            <input className="inp" style={{marginBottom:0}} placeholder={lang==='de'?'z.B. RE-AB 1234':'e.g. RE-AB 1234'} value={newProfilePlate} onChange={e=>setNewProfilePlate(e.target.value)}/>
            <button type="button" onClick={async()=>{await addCarrierPlate(newProfilePlate);setNewProfilePlate('');}} style={{padding:'0 18px',background:'#e8f01a',color:'#0a0c0f',border:'none',borderRadius:11,fontWeight:700,cursor:'pointer',flexShrink:0}}>+</button>
          </div>
          {carrierPlates.length>0?(
            <div style={{marginBottom:13}}>
              {carrierPlates.map((plate,idx)=>(
                <div key={idx} style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#181d25',border:'1px solid #232b36',borderRadius:8,padding:'8px 12px',marginBottom:6}}>
                  <span style={{fontSize:13,fontWeight:600,color:'#e2e8f0'}}>{plate}</span>
                  <button type="button" onClick={()=>removeCarrierPlate(plate)} style={{background:'none',border:'none',color:'#fc8181',cursor:'pointer',fontSize:16}}>✕</button>
                </div>
              ))}
            </div>
          ):(
            <div style={{fontSize:12,color:T.soft,marginBottom:13}}>{lang==='de'?'Noch keine Fahrzeuge.':'No vehicles yet.'}</div>
          )}
        </>
      )}
      <div className="sec-lbl">{t(lang,'changePasswordTitle')}</div>
      <div style={{position:'relative'}}><input className="inp" type={showNewPass?'text':'password'} placeholder={t(lang,'newPasswordPh')} value={newPassword} onChange={e=>setNewPassword(e.target.value)} style={{paddingLeft:42}}/><button type="button" onClick={()=>setShowNewPass(!showNewPass)} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:16,padding:0,color:'#8892a4'}}>{showNewPass?'🙈':'👁'}</button></div>
      <div style={{position:'relative'}}><input className="inp" type={showNewPass2?'text':'password'} placeholder={t(lang,'repeatPasswordPh')} value={newPassword2} onChange={e=>setNewPassword2(e.target.value)} style={{paddingLeft:42}}/><button type="button" onClick={()=>setShowNewPass2(!showNewPass2)} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:16,padding:0,color:'#8892a4'}}>{showNewPass2?'🙈':'👁'}</button></div>
      {newPassword&&newPassword2&&newPassword!==newPassword2&&<div style={{fontSize:12,color:'#fc8181',marginBottom:8,marginTop:-4}}>{t(lang,'passwordsMismatch')}</div>}
      <button className="btn ba" disabled={newPassword.length<6||newPassword!==newPassword2} onClick={changePassword}>{t(lang,'savePassword')}</button>
      <button className="btn bo" onClick={()=>{setShowProfile(false);setNewPassword('');setNewPassword2('');}}>{t(lang,'close')}</button>
    </Overlay>);
    if(!modal) return null;
    if(modal==='gate') return(<Overlay onClose={()=>{if(window.confirm(lang==='de'?'Moechten Sie wirklich schliessen? Ihre Eingaben gehen verloren.':'Are you sure you want to close? Your entries will be lost.')){close();}}}>
      <Elements stripe={stripePromise}>
      <GateModal
        screen={screen} setScreen={setScreen} userRole={userRole} setUserRole={setUserRole}
        driverName={driverName} setDriverName={setDriverName}
        driverPhone={driverPhone} setDriverPhone={setDriverPhone}
        driverOtp={driverOtp} setDriverOtp={setDriverOtp}
        driverPass={driverPass} setDriverPass={setDriverPass}
        driverVehicle={driverVehicle} setDriverVehicle={setDriverVehicle}
        driverPlate={driverPlate} setDriverPlate={setDriverPlate}
        driverCapacity={driverCapacity} setDriverCapacity={setDriverCapacity}
        selectedVehicle={selectedVehicle}
        companyEmail={companyEmail} setCompanyEmail={setCompanyEmail}
        companyEmailOtp={companyEmailOtp} setCompanyEmailOtp={setCompanyEmailOtp}
        companyPass={companyPass} setCompanyPass={setCompanyPass}
        companyPass2={companyPass2} setCompanyPass2={setCompanyPass2}
        companyName={companyName} setCompanyName={setCompanyName}
        companyReg={companyReg} setCompanyReg={setCompanyReg}
        companyVat={companyVat} setCompanyVat={setCompanyVat}
        companyActivity={companyActivity} setCompanyActivity={setCompanyActivity}
        companyAddress={companyAddress} setCompanyAddress={setCompanyAddress}
        companyPostal={companyPostal} setCompanyPostal={setCompanyPostal}
        companyPostalResults={companyPostalResults} companyPostalStatus={companyPostalStatus}
        companySelectedCity={companySelectedCity} setCompanySelectedCity={setCompanySelectedCity}
        companyStreetAddress={companyStreetAddress} setCompanyStreetAddress={setCompanyStreetAddress}
        companyGeoStatus={companyGeoStatus} companyCoords={companyCoords}
        searchPostalCode={searchPostalCode}
        companyPhone={companyPhone} setCompanyPhone={setCompanyPhone}
        companyWebsite={companyWebsite} setCompanyWebsite={setCompanyWebsite}
        companyVehicles={companyVehicles} companyCargo={companyCargo}
        companyPlates={companyPlates} setCompanyPlates={setCompanyPlates}
        companyDocFile={companyDocFile} setCompanyDocFile={setCompanyDocFile}
        companyDocUploading={companyDocUploading} setCompanyDocUploading={setCompanyDocUploading}
        toggleCV={toggleCV} toggleCC={toggleCC}
        loginContact={loginContact} setLoginContact={setLoginContact}
        loginPass={loginPass} setLoginPass={setLoginPass}
        setUserStep={setUserStep} close={close} resetAuth={resetAuth} setModal={setModal}
        authSignUp={authSignUp} authSignIn={authSignIn} saveDriverToDb={saveDriverToDb}
        loadUserRole={loadUserRole} setAuthUser={setAuthUser}
        supabase={supabase} STEP={STEP}
        VEHICLE_TYPES={VEHICLE_TYPES} CARGO_TYPES={CARGO_TYPES} lang={lang}
      />
      </Elements>
    </Overlay>);
    if(modal==='new_load') return(<Overlay onClose={()=>{if(window.confirm(lang==='de'?'Möchten Sie wirklich schließen? Ihre Eingaben gehen verloren.':'Are you sure you want to close? Your entries will be lost.')){resetNewLoad();close();}}}>
      <Elements stripe={stripePromise}>
      <NewLoadModal
        authUser={authUser} nlPickupCoords={nlPickupCoords} myCompanyLat={myCompanyLat} myCompanyLng={myCompanyLng}
        nlGeneratedRef={nlGeneratedRef}
        ensureLoadRef={ensureLoadRef}
        newLoadStep={newLoadStep} setNewLoadStep={setNewLoadStep}
        nlFrom={nlFrom} setNlFrom={setNlFrom} nlTo={nlTo} setNlTo={setNlTo}
        nlDate={nlDate} setNlDate={setNlDate} nlTime={nlTime} setNlTime={setNlTime}
        nlReceiverName={nlReceiverName} setNlReceiverName={setNlReceiverName}
        nlPickupAddress={nlPickupAddress} setNlPickupAddress={setNlPickupAddress}
        nlDeliveryAddress={nlDeliveryAddress} setNlDeliveryAddress={setNlDeliveryAddress}
        pickupAddrStatus={pickupAddrStatus} deliveryAddrStatus={deliveryAddrStatus}
        nlFromPostal={nlFromPostal} setNlFromPostal={setNlFromPostal}
        nlToPostal={nlToPostal} setNlToPostal={setNlToPostal}
        nlFromResults={nlFromResults} nlToResults={nlToResults}
        nlFromSelected={nlFromSelected} nlToSelected={nlToSelected}
        fromPostalStatus={fromPostalStatus} toPostalStatus={toPostalStatus}
        selectFromPostal={selectFromPostal} selectToPostal={selectToPostal}
        nlCargoType={nlCargoType} setNlCargoType={setNlCargoType}
        nlPkgType={nlPkgType} setNlPkgType={setNlPkgType}
        nlPallets={nlPallets} setNlPallets={setNlPallets}
        nlPalletWeight={nlPalletWeight} setNlPalletWeight={setNlPalletWeight}
        nlLength={nlLength} setNlLength={setNlLength}
        nlWidth={nlWidth} setNlWidth={setNlWidth}
        nlHeight={nlHeight} setNlHeight={setNlHeight}
        nlWeight={nlWeight} setNlWeight={setNlWeight}
        nlVehicle={nlVehicle} setNlVehicle={setNlVehicle}
        nlPrice={nlPrice} setNlPrice={setNlPrice}
        nlCargoValue={nlCargoValue} setNlCargoValue={setNlCargoValue}
        serverDate={serverDate}
        nlInsured={nlInsured} setNlInsured={setNlInsured}
        nlInsuranceMode={nlInsuranceMode} setNlInsuranceMode={setNlInsuranceMode}
        nlPayerType={nlPayerType} setNlPayerType={setNlPayerType}
        nlPayerEmail={nlPayerEmail} setNlPayerEmail={setNlPayerEmail}
        nlPayerName={nlPayerName} setNlPayerName={setNlPayerName}
        nlNotes={nlNotes} setNlNotes={setNlNotes}
        submitNewLoad={submitNewLoad} close={close}
        CITY_NAMES={CITY_NAMES} CARGO_TYPES={CARGO_TYPES} VEHICLE_TYPES={VEHICLE_TYPES} lang={lang}
      />
      </Elements>
    </Overlay>);
    if(modal==='accept_load') return(<Overlay onClose={()=>{resetAccept();close();}}>
      <Elements stripe={stripePromise}>
        <AcceptLoadModal
        selLoad={selLoad} acceptStep={acceptStep} setAcceptStep={setAcceptStep}
        userRole={userRole} driverPlate={driverPlate} carrierPlates={carrierPlates}
        insPolicyNum={insPolicyNum} setInsPolicyNum={setInsPolicyNum}
        insCompany={insCompany} setInsCompany={setInsCompany}
        insExpiry={insExpiry} setInsExpiry={setInsExpiry}
        insEuloadPaid={insEuloadPaid} setInsEuloadPaid={setInsEuloadPaid}
        photos={photos} setPhotos={setPhotos}
        sigSender={sigSender} setSigSender={setSigSender}
        sigSenderName={sigSenderName} setSigSenderName={setSigSenderName}
        sigDriver={sigDriver} setSigDriver={setSigDriver}
        driverName={driverName}
        close={close} saveShipmentToDb={saveShipmentToDb}
        setActiveLoad={setActiveLoad} setAvail={(v)=>{setAvail(v);}} resetAccept={resetAccept}
        lang={lang}
      />
      </Elements>
    </Overlay>);
    if(modal==='delivery') return(<Overlay onClose={close}>
      <DeliveryModal
        activeLoad={activeLoad} selLoad={selLoad}
        deliveryStep={deliveryStep} setDeliveryStep={setDeliveryStep}
        deliveryOk={deliveryOk} setDeliveryOk={setDeliveryOk}
        damageNote={damageNote} setDamageNote={setDamageNote}
        deliveryPhotos={deliveryPhotos} setDeliveryPhotos={setDeliveryPhotos}
        sigReceiver={sigReceiver} setSigReceiver={setSigReceiver}
        sigReceiverName={sigReceiverName} setSigReceiverName={setSigReceiverName}
        close={close} setAvail={setAvail} setActiveLoad={setActiveLoad} resetDelivery={resetDelivery} completeDelivery={completeDelivery}
        lang={lang}
      />
    </Overlay>);

    if(modal==='payment') return(
      <Overlay onClose={close}>
      <Elements stripe={stripePromise}>
        <MembershipPaymentContent
          userRole={userRole} lang={lang}
          authUser={authUser} companyEmail={companyEmail}
          setUserStep={setUserStep} setModal={setModal} close={close}
          STEP={STEP} T={T}
        />
      </Elements>
      </Overlay>
    );
    if(modal==='docs'){
      const driverDocs=[{l:'ID Card or Passport',i:'🪪'},{l:'Driver Licence CE',i:'📋'},{l:'Liability Insurance',i:'🛡️'},{l:'Vehicle Registration',i:'🚛'}];
      const carrierDocs=[{l:'Company Registration',i:'🏢'},{l:'VAT Certificate',i:'📄'},{l:'Liability Insurance',i:'🛡️'},{l:'Fleet Documentation',i:'🚛'}];
      const shipperDocs=[{l:'Company Registration',i:'🏢'},{l:'VAT Certificate',i:'📄'},{l:'Liability Insurance',i:'🛡️'},{l:'Business Activity Certificate',i:'📋'}];
      const required=userRole==='driver'?driverDocs:userRole==='carrier'?carrierDocs:shipperDocs;
      return(
        <Overlay onClose={close}>
          <div tabIndex={-1} onKeyDown={e=>{ if(e.key==='Enter'||e.keyCode===13||e.which===13){ document.getElementById('docsContinueBtn')?.click(); } }}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:13,background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.2)',borderRadius:9,padding:'9px 12px'}}><span>✓</span><div><div style={{fontSize:11,color:'#86efac',fontWeight:600}}>{t(lang,'paymentConfirmed')}</div><div style={{fontSize:11,color:T.soft}}>{t(lang,'uploadVerificationDocs')}</div></div></div>
          <div className="ttl">{t(lang,'uploadDocsTitle')}</div><div className="sub">{lang==='de'?'Diese Dokumente sind optional und können jederzeit später ergänzt werden.':'These documents are optional and can be completed later.'}</div>
          {required.map(({l,i})=>(
            <div key={l} className={'dr'+(docs.includes(l)?' done':'')} style={{flexDirection:'column',alignItems:'stretch',gap:8}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}><span style={{fontSize:16}}>{i}</span><span style={{fontSize:13,fontWeight:500}}>{l}</span></div>
                <span style={{fontSize:16,color:docs.includes(l)?'#22c55e':T.soft}}>{docUploading[l]?'⏳':docs.includes(l)?'✓':''}</span>
              </div>
              <div style={{display:'flex',gap:8}}>
                <label style={{flex:1,textAlign:'center',padding:'8px',background:'rgba(232,240,26,0.08)',border:'1px solid rgba(232,240,26,0.25)',borderRadius:8,fontSize:11,fontWeight:600,color:'#e8f01a',cursor:'pointer'}}>
                  📷 {lang==='de'?'Kamera':'Camera'}
                  <input type="file" accept="image/*" capture="environment" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f) uploadCompanyDoc(l,f);}}/>
                </label>
                <label style={{flex:1,textAlign:'center',padding:'8px',background:'rgba(136,146,164,0.08)',border:'1px solid rgba(136,146,164,0.25)',borderRadius:8,fontSize:11,fontWeight:600,color:'#cbd5e0',cursor:'pointer'}}>
                  🖼️ {lang==='de'?'Galerie':'Gallery'}
                  <input type="file" accept="image/*,.pdf" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f) uploadCompanyDoc(l,f);}}/>
                </label>
              </div>
            </div>
          ))}
          <div style={{fontSize:11,color:T.soft,textAlign:'center',marginBottom:10}}>{docs.length}/{required.length} {lang==='de'?'hochgeladen':'uploaded'}</div>
          <button id="docsContinueBtn" className="btn ba" style={{marginTop:8}} onClick={()=>{setUserStep(STEP.DOCS_SENT);close();}}>{lang==='de'?'Weiter zu den Ladungen':'Continue to loads'}</button></div>
        </Overlay>
      );
    }
    if(modal==='pending') return(
      <Overlay onClose={close}>
        <div className="ctr">
          <div className="bico" style={{background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.3)'}}>⏳</div>
          {docs.length===0?(<>
            <div className="ttl">{lang==='de'?'Dokumente erforderlich':'Documents required'}</div>
            <div className="sub">{lang==='de'?'Bitte laden Sie Ihre Dokumente hoch, damit unser Team Ihr Konto pruefen kann.':'Please upload your documents so our team can review your account.'}</div>
            <button className="btn ba" onClick={()=>{close();setModal('docs');}}>{lang==='de'?'Dokumente hochladen':'Upload documents'}</button>
          </>):(<>
            <div className="ttl">{t(lang,'verificationInProgressTitle')}</div>
            <div className="sub">{t(lang,'verificationSub1')} <strong style={{color:T.text}}>{t(lang,'verificationSub2')}</strong>. {t(lang,'verificationSub3')} {userRole==='driver'?t(lang,'smsWord'):t(lang,'emailWord')} {t(lang,'verificationSub4')}</div>
            <div className="pb" style={{textAlign:'left'}}>{[t(lang,'reviewStep1'),t(lang,'reviewStep2'),t(lang,'reviewStep3')].map((s,idx)=>(<div key={idx} style={{fontSize:13,marginBottom:8,display:'flex',gap:8}}><span style={{color:T.accent,fontWeight:700,minWidth:16}}>{idx+1}.</span>{s}</div>))}</div>
            <button className="btn ba" onClick={close}>{t(lang,'gotIt')}</button>
          </>)}
        </div>
      </Overlay>
    );
    if(modal==='detail'&&selLoad){
      const b=BADGE[selLoad.type]||BADGE['Curtainside'];
      const overCapacity=userRole!=='shipper'&&!selLoad.isMine&&parseFloat(driverCapacity)>0&&selLoad.weight>parseFloat(driverCapacity);
      return(
        <Overlay onClose={close}>
          {!overCapacity&&(<>
          {selLoad.loadRef&&<div style={{fontSize:11,color:'#4a5568',fontFamily:'Barlow Condensed',letterSpacing:1,marginBottom:8}}>REF: {selLoad.loadRef}</div>}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:11}}>
            <span className="tbg" style={{background:b.bg,color:b.color,borderColor:b.border}}>{selLoad.type}</span>
            <span className="price">{selLoad.price.toLocaleString()} EUR</span>
          </div>
          <div style={{fontFamily:'Barlow Condensed',fontSize:22,fontWeight:900,marginBottom:7,color:'#f1f5f9'}}>{selLoad.from} {t(lang,'toCity')} {selLoad.to}</div>
          <div style={{display:'flex',gap:6,marginBottom:16,flexWrap:'wrap'}}>
            <span className="vt">{t(lang,'verified')}</span>
            {selLoad.insured?<span className="it">{t(lang,'insured')}</span>:<span className="at">⚡ {t(lang,'weInsure')}</span>}
          </div>
          <div className="pb">
            {[[t(lang,'shipper'),selLoad.shipper],[t(lang,'distance'),selLoad.distance+' km'],[t(lang,'weight'),selLoad.weight+' kg'],[t(lang,'departure'),selLoad.depart],[t(lang,'estArrival'),selLoad.arrive],[t(lang,'payment'),t(lang,'netDays')]].map(([k,v])=>(<div key={k} className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{k}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{v}</span></div>))}
          </div>
          </>)}
          {selLoad.isMine
            ?<>
              {loadShipment?(
                <div style={{background:'rgba(59,130,246,0.07)',border:'1px solid rgba(59,130,246,0.25)',borderRadius:10,padding:13,marginBottom:10}}>
                  <div style={{fontSize:12,color:'#93c5fd',fontWeight:700,marginBottom:10}}>🚛 {t(lang,'driverAcceptedTitle')}</div>
                  <div className="prow" style={{marginBottom:6}}><span style={{fontSize:11,color:T.soft}}>👤 {t(lang,'driverNameLbl')}</span><span style={{fontSize:12,fontWeight:600}}>{loadShipment.driver_name||'—'}</span></div>
                  <div className="prow" style={{marginBottom:6}}><span style={{fontSize:11,color:T.soft}}>🔢 {t(lang,'plateLbl')}</span><span style={{fontSize:12,fontWeight:700,fontFamily:'Barlow Condensed',letterSpacing:1,color:'#e8f01a'}}>{loadShipment.driver_plate||'—'}</span></div>
                  <div className="prow" style={{marginBottom:6}}><span style={{fontSize:11,color:T.soft}}>🚚 {t(lang,'vehicleTypeLbl')}</span><span style={{fontSize:12,fontWeight:600}}>{loadShipment.driver_vehicle||'—'}</span></div>
                  <div className="prow"><span style={{fontSize:11,color:T.soft}}>📞 {t(lang,'contactLbl')}</span><span style={{fontSize:12,fontWeight:600,color:'#93c5fd'}}>{loadShipment.driver_phone||'—'}</span></div>
                  <div style={{fontSize:10,color:'#fcd34d',marginTop:10,lineHeight:1.6,borderTop:'1px solid #232b36',paddingTop:8}}>⚠️ {t(lang,'matchWarning')}</div>
                </div>
              ):selLoad.status==='pending'?(
                <div style={{background:'rgba(245,158,11,0.07)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:10,padding:13,marginBottom:10}}>
                  <div style={{fontSize:12,color:'#fcd34d',fontWeight:700,marginBottom:10}}>⏳ {t(lang,'driverRequestedTitle')}</div>
                  <div className="prow" style={{marginBottom:6}}><span style={{fontSize:11,color:T.soft}}>👤 {t(lang,'nameLbl')}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{selLoad.requested_driver_name||'—'}</span></div>
                  <div className="prow" style={{marginBottom:6}}><span style={{fontSize:11,color:T.soft}}>🔢 {t(lang,'plateLbl')}</span><span style={{fontSize:12,fontWeight:700,fontFamily:'Barlow Condensed',letterSpacing:1,color:'#e8f01a'}}>{selLoad.requested_driver_plate||'—'}</span></div>
                  <div className="prow"><span style={{fontSize:11,color:T.soft}}>📞 {t(lang,'contactLbl')}</span><span style={{fontSize:12,fontWeight:600,color:'#93c5fd'}}>{selLoad.requested_driver_phone||'—'}</span></div>
                </div>
              ):selLoad.status==='assigned'?(
                <div style={{background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:10,padding:11,fontSize:12,color:'#86efac',textAlign:'center',marginBottom:10}}>✓ {t(lang,'driverConfirmed')}: {selLoad.requested_driver_name} — {t(lang,'plateLbl')} {selLoad.requested_driver_plate}</div>
              ):(
                <div style={{background:'rgba(232,240,26,0.05)',border:'1px solid rgba(232,240,26,0.2)',borderRadius:10,padding:11,fontSize:12,color:'#e8f01a',textAlign:'center',marginBottom:10}}>{t(lang,'yourLoadWaiting')}</div>
              )}
              {selLoad.status==='pending'&&(
                <>
                  <button className="btn bs" onClick={()=>acceptRequest(selLoad)}>✓ {t(lang,'confirmDriver')}</button>
                  <button className="btn bdan" onClick={()=>rejectRequest(selLoad)}>✕ {t(lang,'rejectRequest')}</button>
                </>
              )}
              {selLoad.status!=='pending'&&selLoad.status!=='assigned'&&<button className="btn bdan" onClick={()=>deleteLoad(selLoad)}>🗑 {t(lang,'deleteLoadBtn')}</button>}
            </>
            :userRole==='shipper'?(
              <div style={{background:'rgba(136,146,164,0.05)',border:'1px solid #232b36',borderRadius:10,padding:11,fontSize:12,color:'#8892a4',textAlign:'center'}}>{t(lang,'shipperNoAccept')}</div>
            ):(parseFloat(driverCapacity)>0&&selLoad.weight>parseFloat(driverCapacity))?(
              <div className="warn-box">⚠️ {t(lang,'overCapacityWarning')}</div>
            ):selLoad.status==='assigned'&&selLoad.requested_by===authUser?.id?(
              <>
                <div style={{background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:10,padding:11,fontSize:12,color:'#86efac',textAlign:'center',marginBottom:10}}>✓ {t(lang,'assignedToYou')}<br/>{t(lang,'goToMyLoads')}</div>
              </>
            ):selLoad.status==='assigned'?(
              <div className="warn-box">{t(lang,'assignedOther')}</div>
            ):selLoad.status==='pending'&&selLoad.requested_by===authUser?.id?(
              <div className="info-pending">⏳ {t(lang,'pendingYours')}</div>
            ):selLoad.status==='pending'?(
              <div className="warn-box">{t(lang,'pendingOther')}</div>
            ):(
              <>
                <button className="btn ba" onClick={()=>requestLoad(selLoad)}>
                  {t(lang,'requestThisLoad')}
                </button>
                <button className="btn bo" onClick={()=>setModal('status')}>{t(lang,'changeStatus')}</button>
              </>
            )
          }
        </Overlay>
      );
    }
    if(modal==='status') return(
      <Overlay onClose={close}>
        <div className="ttl">{t(lang,'tripStatusTitle')}</div><div className="sub">{t(lang,'tripStatusSub')}</div>
        <button className="btn bs" onClick={()=>{setReadyState('free');close();}}>🟢  {t(lang,'availableForLoads')}</button>
        <button className="btn bdan" onClick={()=>{setReadyState('loaded');close();}}>🔴  {t(lang,'currentlyOnTrip')}</button>
      </Overlay>
    );
    if(modal==='tracking'&&selectedTrackLoad){
  const progress=trackingProgress[selectedTrackLoad.id]||0;
  const progressPct=Math.round(progress*100);
  const remainingKm=Math.round((selectedTrackLoad.distance||0)*(1-progress));
  const etaHours=Math.round(remainingKm/70);
  const vehicleInfo=VEHICLE_TYPES.find(v=>v.k===selectedTrackLoad.driverVehicle);
  return(
    <Overlay onClose={close}>
      <div style={{fontFamily:'Barlow Condensed',fontSize:11,color:'#3b82f6',letterSpacing:1,marginBottom:5}}>📍 LIVE TRACKING · REF {selectedTrackLoad.loadRef}</div>
      <div className="ttl">{selectedTrackLoad.from} → {selectedTrackLoad.to}</div>
      <div className="sub">{t(lang,'lastUpdateNow')}</div>
      {trackedLoads.length>1&&(
        <div style={{display:'flex',gap:6,marginBottom:13,overflowX:'auto'}}>
          {trackedLoads.map(l=>(
            <button key={l.id} onClick={()=>setSelectedTrackLoad(l)}
              style={{padding:'6px 11px',borderRadius:8,whiteSpace:'nowrap',cursor:'pointer',
                background:l.id===selectedTrackLoad.id?'rgba(59,130,246,0.2)':'#181d25',
                border:'1px solid '+(l.id===selectedTrackLoad.id?'#3b82f6':'#232b36'),
                color:l.id===selectedTrackLoad.id?'#93c5fd':'#8892a4',
                fontSize:11,fontWeight:600,fontFamily:'Barlow Condensed'}}>
              {l.loadRef}
            </button>
          ))}
        </div>
      )}
      <TrackingMap trackedLoad={selectedTrackLoad} progress={progress}/>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
        <span style={{fontSize:11,color:T.soft}}>{t(lang,'tripProgress')}</span>
        <span style={{fontFamily:'Barlow Condensed',fontSize:15,fontWeight:900,color:'#3b82f6'}}>{progressPct}%</span>
      </div>
      <div style={{height:6,background:'#232b36',borderRadius:3,overflow:'hidden',margin:'8px 0'}}>
        <div style={{height:'100%',background:'linear-gradient(90deg,#22c55e,#e8f01a)',width:progressPct+'%',transition:'width 0.5s'}}/>
      </div>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:T.soft,marginBottom:14}}>
        <span>🟢 {selectedTrackLoad.from}</span>
        <span>{remainingKm} km · ETA ~{etaHours}h</span>
        <span>🔴 {selectedTrackLoad.to}</span>
      </div>
      <div className="sec-lbl">{t(lang,'driverInfo')}</div>
      <div className="pb">
        <div className="prow"><span style={{fontSize:11,color:T.soft}}>👤 {t(lang,'driverNameLbl')}</span><span style={{fontSize:12,fontWeight:600}}>{selectedTrackLoad.driverName}</span></div>
        <div className="prow"><span style={{fontSize:11,color:T.soft}}>🚛 {t(lang,'vehicleTypeLbl2')}</span><span style={{fontSize:12,fontWeight:600}}>{vehicleInfo?vehicleInfo.icon+' '+vehicleInfo.name:'—'}</span></div>
        <div className="prow"><span style={{fontSize:11,color:T.soft}}>🔢 {t(lang,'plateLbl')}</span><span style={{fontSize:12,fontWeight:600,fontFamily:'Barlow Condensed',letterSpacing:1}}>{selectedTrackLoad.driverPlate}</span></div>
        <div className="prow"><span style={{fontSize:11,color:T.soft}}>📞 {t(lang,'contactLbl')}</span><span style={{fontSize:12,fontWeight:600,color:'#93c5fd'}}>{selectedTrackLoad.driverPhone}</span></div>
      </div>
      <div style={{background:'rgba(59,130,246,0.05)',border:'1px solid rgba(59,130,246,0.15)',borderRadius:10,padding:10,marginBottom:14,fontSize:11,color:T.soft,lineHeight:1.7,textAlign:'center',whiteSpace:'pre-line'}}>
        🛡 {t(lang,'liveTrackingNote')}
      </div>
      <button className="btn bo" onClick={close}>{t(lang,'close')}</button>
    </Overlay>
  );
}
if(modal==='real_tracking') return(
      <Overlay onClose={close}>
        <div className="ttl">{t(lang,'realTrackingTitle')}</div>
        {realTrackingLoad&&(
          <div style={{background:'rgba(136,146,164,0.05)',border:'1px solid #232b36',borderRadius:10,padding:11,marginBottom:14}}>
            <div style={{fontSize:11,color:'#e8f01a',fontFamily:'Barlow Condensed',letterSpacing:1}}>REF: {realTrackingLoad.load_ref}</div>
            <div style={{fontSize:13,fontWeight:600,marginTop:4}}>{realTrackingLoad.from_city} → {realTrackingLoad.to_city}</div>
            {realTrackingLoad.requested_driver_name&&(
              <div style={{fontSize:11,color:T.soft,marginTop:6}}>{realTrackingLoad.requested_driver_name} — {realTrackingLoad.requested_driver_plate}</div>
            )}
          </div>
        )}
        {realTrackingLoad&&realTrackingLoad.driver_lat&&realTrackingLoad.driver_lng?(
          <>
            <div className="pb">
              <div className="prow"><span style={{fontSize:11,color:T.soft}}>{t(lang,'coordinatesLbl')}</span><span style={{fontSize:12,fontWeight:600}}>{realTrackingLoad.driver_lat.toFixed(5)}, {realTrackingLoad.driver_lng.toFixed(5)}</span></div>
              <div className="prow"><span style={{fontSize:11,color:T.soft}}>{t(lang,'lastUpdatePrefix')}</span><span style={{fontSize:12,fontWeight:600}}>{realTrackingLoad.location_updated_at?new Date(realTrackingLoad.location_updated_at).toLocaleTimeString():'—'}</span></div>
            </div>
            <a href={`https://www.google.com/maps?q=${realTrackingLoad.driver_lat},${realTrackingLoad.driver_lng}`} target="_blank" rel="noopener noreferrer" className="btn ba" style={{textAlign:'center',textDecoration:'none',display:'block'}}>🗺 {t(lang,'openInGoogleMaps')}</a>
          </>
        ):(
          <div style={{background:'rgba(245,158,11,0.07)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:10,padding:15,marginBottom:14,fontSize:12,color:'#fcd34d',textAlign:'center',lineHeight:1.7}}>
            ⏳ {t(lang,'waitingForGpsSignal')}<br/>
            <span style={{fontSize:11,color:T.soft}}>{t(lang,'noLocationYet')}</span>
          </div>
        )}
        <button className="btn bo" onClick={close}>{t(lang,'close')}</button>
      </Overlay>
    );
    if(modal==='busy_warning') return(
      <Overlay onClose={close}>
        <div className="ctr">
          <div className="bico" style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)'}}>🚛</div>
          <div className="ttl">{t(lang,'busyWarningModalTitle')}</div>
          <div className="sub" style={{lineHeight:1.8,whiteSpace:'pre-line'}}>{t(lang,'busyWarningModalSub')}</div>
          <button className="btn bs" onClick={()=>{refreshTripState();close();}}>🟢 {t(lang,'deliveredNowFreeBtn')}</button>
          <button className="btn bo" onClick={close}>{t(lang,'closeBtn')}</button>
        </div>
      </Overlay>
    );
   return null;
  };





  if(verifyLoadRef) return(
    <>
      <style>{CSS}</style>
      <div className="app">
        <div style={{maxWidth:480,margin:'0 auto',padding:24}}>
          <div className="logo" style={{marginBottom:20}}>EU<span>.</span>LOAD <span style={{fontSize:13,color:'#8892a4',fontFamily:'Barlow',letterSpacing:0}}>VERIFY</span></div>
          {verifyLoading?(
            <div style={{textAlign:'center',padding:60,color:T.soft}}>{lang==='de'?'Wird geladen...':'Loading...'}</div>
          ):verifyError?(
            <div className="warn-box">
              {verifyError==='not_found'?(lang==='de'?'Diese Frachtreferenz wurde nicht gefunden.':'This load reference was not found.'):
               verifyError==='not_available'?(lang==='de'?'Diese Fracht ist derzeit nicht zur Überprüfung verfügbar.':'This load is not currently available for verification.'):
               verifyError==='expired'?(lang==='de'?'Der Überprüfungszeitraum für diese Fracht ist abgelaufen.':'The verification window for this load has expired.'):
               (lang==='de'?'Ein Fehler ist aufgetreten.':'An error occurred.')}
            </div>
          ):verifyData&&(
            <>
              <div style={{background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:10,padding:13,marginBottom:16,fontSize:13,color:'#86efac',textAlign:'center',fontWeight:700}}>✓ {lang==='de'?'Verifizierte Fracht':'Verified Shipment'}</div>
              <div className="pb">
                <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Referenz':'Reference'}</span><span style={{fontSize:12,fontWeight:700}}>{verifyData.load_ref}</span></div>
                <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Strecke':'Route'}</span><span style={{fontSize:12,fontWeight:600}}>{verifyData.from_city} → {verifyData.to_city}</span></div>
                {verifyData.pickup_address&&<div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Abholadresse':'Pickup address'}</span><span style={{fontSize:12,fontWeight:600,textAlign:'right',maxWidth:'60%'}}>{verifyData.pickup_address}</span></div>}
                {verifyData.delivery_address&&<div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Lieferadresse':'Delivery address'}</span><span style={{fontSize:12,fontWeight:600,textAlign:'right',maxWidth:'60%'}}>{verifyData.delivery_address}</span></div>}
                <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Gewicht':'Weight'}</span><span style={{fontSize:12,fontWeight:600}}>{verifyData.total_weight} kg</span></div>
                <div className="prow"><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Frachtart':'Cargo type'}</span><span style={{fontSize:12,fontWeight:600}}>{verifyData.cargo_type||'—'}</span></div>
              </div>

              <div className="sec-lbl">{lang==='de'?'Fahrzeug & Fahrer':'Vehicle & Driver'}</div>
              <div className="pb">
                <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>👤 {lang==='de'?'Fahrer':'Driver'}</span><span style={{fontSize:12,fontWeight:600}}>{verifyData.driver_name||'—'}</span></div>
                <div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>🔢 {lang==='de'?'Kennzeichen':'Plate'}</span><span style={{fontSize:12,fontWeight:700,fontFamily:'Barlow Condensed',letterSpacing:1,color:'#e8f01a'}}>{verifyData.driver_plate||'—'}</span></div>
                <div className="prow"><span style={{fontSize:11,color:T.soft}}>🚚 {lang==='de'?'Fahrzeugtyp':'Vehicle type'}</span><span style={{fontSize:12,fontWeight:600}}>{verifyData.driver_vehicle||'—'}</span></div>
              </div>

              {verifyData.sig_sender&&(
                <div style={{marginBottom:13}}>
                  <div className="sec-lbl">{lang==='de'?'Unterschrift Absender':'Sender Signature'}</div>
                  <img src={verifyData.sig_sender} alt="sender signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
                </div>
              )}
              {verifyData.sig_driver&&(
                <div style={{marginBottom:13}}>
                  <div className="sec-lbl">{lang==='de'?'Unterschrift Fahrer':'Driver Signature'}</div>
                  <img src={verifyData.sig_driver} alt="driver signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
                </div>
              )}
              {verifyData.cargo_photo_urls&&verifyData.cargo_photo_urls.length>0&&(
                <div style={{marginBottom:13}}>
                  <div className="sec-lbl">{lang==='de'?'Fotos bei Abholung':'Pickup Photos'}</div>
                  <div className="photo-grid">
                    {verifyData.cargo_photo_urls.map((url,idx)=>(
                      <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt={'cargo-photo'+idx} style={{width:'100%',height:80,objectFit:'cover',borderRadius:10,border:'1px solid #232b36'}}/>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {verifyData.sig_receiver&&(
                <div style={{marginBottom:13}}>
                  <div className="sec-lbl">{lang==='de'?'Unterschrift Empfänger':'Receiver Signature'}</div>
                  <img src={verifyData.sig_receiver} alt="receiver signature" style={{width:'100%',background:'#fff',borderRadius:10}}/>
                </div>
              )}
              {verifyData.delivery_photo_urls&&verifyData.delivery_photo_urls.length>0&&(
                <div style={{marginBottom:13}}>
                  <div className="sec-lbl">{lang==='de'?'Fotos bei Lieferung':'Delivery Photos'}</div>
                  <div className="photo-grid">
                    {verifyData.delivery_photo_urls.map((url,idx)=>(
                      <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt={'delivery-photo'+idx} style={{width:'100%',height:80,objectFit:'cover',borderRadius:10,border:'1px solid #232b36'}}/>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {(verifyData.shipper_address||verifyData.shipper_website||verifyData.shipper_phone||verifyData.shipper_email)&&(
                <div style={{background:'rgba(34,197,94,0.06)',border:'1px solid rgba(34,197,94,0.2)',borderRadius:10,padding:12,marginTop:16,fontSize:11,color:'#86efac',textAlign:'center',lineHeight:1.8}}>
                  {lang==='de'?'Absender-Kontakt:':'Sender contact:'}<br/>
                  <strong>{verifyData.shipper_name||'—'}</strong><br/>
                  {verifyData.shipper_address&&<>{verifyData.shipper_address}<br/></>}
                  {verifyData.shipper_email&&<>{verifyData.shipper_email}<br/></>}
                  {verifyData.shipper_phone&&<>{verifyData.shipper_phone}<br/></>}
                  {verifyData.shipper_website&&<>{verifyData.shipper_website}</>}
                </div>
              )}
              <div style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.2)',borderRadius:10,padding:12,marginTop:16,fontSize:11,color:'#93c5fd',textAlign:'center',lineHeight:1.8}}>
                {lang==='de'?'Plattformbetreiber:':'Platform operator:'}<br/>
                <strong>Imperial Nexus GmbH</strong><br/>
                Ostertor 36, 31180 Giesen<br/>
                info@imperialnexus.de<br/>
                +49 5121 7550116<br/>
                www.imperialnexus.de
              </div>
              <div style={{fontSize:11,color:'#4a5568',textAlign:'center',marginTop:12}}>{lang==='de'?'Dieses Dokument dient ausschließlich zur behördlichen Überprüfung.':'This document is intended solely for official verification purposes.'}</div>
            </>
          )}
        </div>
      </div>
    </>
  );

  if(payerToken) return(
    <>
      <style>{CSS}</style>
      <div className="app">
        {payerLoadLoading?(
          <div style={{textAlign:'center',padding:60,color:T.soft}}>{lang==='de'?'Wird geladen...':'Loading...'}</div>
        ):!payerLoad?(
          <div style={{maxWidth:420,margin:'60px auto',padding:24,textAlign:'center'}}>
            <div className="warn-box">{lang==='de'?'Zahlungslink ungültig oder abgelaufen.':'Payment link invalid or expired.'}</div>
          </div>
        ):payerLoad.status!=='awaiting_payer_payment'?(
          <div style={{maxWidth:420,margin:'60px auto',padding:24,textAlign:'center'}}>
            <div className="success-ico" style={{background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.3)'}}>✓</div>
            <div className="ttl">{lang==='de'?'Bereits bezahlt':'Already paid'}</div>
            <div className="sub">{lang==='de'?'Für diese Ladung wurde die Zahlung bereits autorisiert.':'Payment for this load has already been authorized.'}</div>
          </div>
        ):(
          <Elements stripe={stripePromise}>
            <PayerPaymentPage load={payerLoad} token={payerToken} lang={lang}/>
          </Elements>
        )}
      </div>
    </>
  );

  if(userRole==='admin') return(
    <>
      <style>{CSS}</style>
      <div className="app">
        <div style={{maxWidth:700,margin:'0 auto',minHeight:'100vh',padding:20}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
            <div className="logo">EU<span>.</span>LOAD <span style={{fontSize:14,color:'#8892a4',fontFamily:'Barlow',letterSpacing:0}}>ADMIN</span></div>
            <button onClick={async()=>{if(window.confirm(lang==='de'?'Abmelden?':'Log out?')){await authSignOut();}}} style={{padding:'6px 14px',background:'rgba(252,129,129,0.1)',color:'#fc8181',border:'1px solid rgba(252,129,129,0.4)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>{t(lang,'logout')}</button>
          </div>
          <div style={{background:'rgba(136,146,164,0.05)',border:'1px solid #232b36',borderRadius:10,padding:14,marginBottom:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:700,color:'#cbd5e0'}}>{lang==='de'?'Aktive Sitzungen':'Active sessions'} ({activeSessions.length})</div>
              <button onClick={loadActiveSessions} style={{padding:'4px 10px',background:'rgba(136,146,164,0.1)',color:'#cbd5e0',border:'1px solid rgba(136,146,164,0.4)',borderRadius:8,fontSize:11,cursor:'pointer'}}>{'\u21BB'} {lang==='de'?'Aktualisieren':'Refresh'}</button>
            </div>
            {activeSessions.length===0?(
              <div style={{fontSize:12,color:'#8892a4'}}>{lang==='de'?'Keine aktiven Sitzungen.':'No active sessions.'}</div>
            ):activeSessions.map(s=>{ const mins=Math.round((Date.now()-new Date(s.last_seen).getTime())/60000); const stale=mins>=10&&!s.in_transit; return(
              <div key={s.user_id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderTop:'1px solid #232b36',gap:8}}>
                <div style={{fontSize:12,color:'#cbd5e0'}}>
                  <b>{s.full_name||s.user_id}</b> <span style={{color:'#8892a4'}}>({s.role||'-'})</span>
                  {s.in_transit&&<span style={{color:'#fc8181'}}> {'\uD83D\uDE9B'} {lang==='de'?'unterwegs':'in transit'}</span>}
                  <div style={{fontSize:11,color:stale?'#f59e0b':'#8892a4'}}>{lang==='de'?'Letztes Signal vor ':'Last signal '}{mins} min{lang==='de'?'':' ago'}{stale?(lang==='de'?' (abgelaufen)':' (expired)'):''}</div>
                </div>
                <button onClick={()=>releaseUserSession(s.user_id,s.full_name)} style={{padding:'6px 10px',background:'rgba(252,129,129,0.1)',color:'#fc8181',border:'1px solid rgba(252,129,129,0.4)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>{lang==='de'?'Freigeben':'Release'}</button>
              </div>
            );})}
          </div>
          <div style={{fontSize:13,color:T.soft,marginBottom:20}}>{lang==='de'?'Ausstehende Verifizierungen':'Pending Verifications'} ({pendingUsers.length})</div>

          {adminLoading?(
            <div style={{textAlign:'center',padding:40,color:T.soft}}>{lang==='de'?'Wird geladen...':'Loading...'}</div>
          ):pendingUsers.length===0?(
            <div style={{background:'rgba(136,146,164,0.05)',border:'1px solid #232b36',borderRadius:10,padding:30,textAlign:'center',color:T.soft}}>{lang==='de'?'Keine ausstehenden Benutzer.':'No pending users.'}</div>
          ):(
            pendingUsers.map(u=>(
              <div key={u.id} className="lc" onClick={()=>openAdminUserDocs(u)} style={{cursor:'pointer'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                  <span className="tbg" style={{background:'rgba(59,130,246,0.15)',color:'#93c5fd',borderColor:'rgba(59,130,246,0.3)'}}>{u.role}</span>
                  <span style={{fontSize:10,color:T.soft}}>{u.created_at?new Date(u.created_at).toLocaleDateString('de-DE'):''}</span>
                </div>
                <div style={{fontSize:15,fontWeight:700,color:'#f1f5f9',marginBottom:3}}>{u.full_name||u.company_name||'—'}</div>
                <div style={{fontSize:12,color:T.soft}}>{u.email||u.phone||'—'}</div>
                <div style={{fontSize:11,color:T.soft,marginTop:6}}>{(u.uploaded_docs||[]).length} {lang==='de'?'Dokument(e)':'document(s)'}</div>
              </div>
            ))
          )}
        </div>
      </div>
      {adminSelectedUser&&(
        <Overlay onClose={()=>setAdminSelectedUser(null)}>
          <div className="ttl">{adminSelectedUser.full_name||adminSelectedUser.company_name}</div>
          <div className="sub">{adminSelectedUser.role} · {adminSelectedUser.email||adminSelectedUser.phone}</div>
          <div className="pb">
            {adminSelectedUser.vehicle_plate&&<div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Kennzeichen':'Plate'}</span><span style={{fontSize:12,fontWeight:700}}>{adminSelectedUser.vehicle_plate}</span></div>}
            {adminSelectedUser.company_reg&&<div className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Handelsregister':'Reg. number'}</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{adminSelectedUser.company_reg}</span></div>}
            {adminSelectedUser.company_vat&&<div className="prow"><span style={{fontSize:11,color:T.soft}}>VAT</span><span style={{fontSize:12,fontWeight:600,color:'#f1f5f9'}}>{adminSelectedUser.company_vat}</span></div>}
          </div>
          {adminSelectedUser.role!=='carrier'&&(<div style={{background:adminSelectedUser.subscription_status==='active'?'rgba(34,197,94,0.08)':'rgba(239,68,68,0.08)',border:'1px solid '+(adminSelectedUser.subscription_status==='active'?'rgba(34,197,94,0.3)':'rgba(239,68,68,0.3)'),borderRadius:10,padding:11,marginBottom:13,fontSize:13,fontWeight:700,color:adminSelectedUser.subscription_status==='active'?'#86efac':'#fc8181'}}>{adminSelectedUser.subscription_status==='active'?'\u2713 ':'\u2717 '}{lang==='de'?'Mitgliedschaft':'Membership'}: {adminSelectedUser.subscription_status==='active'?(lang==='de'?'bezahlt':'paid'):(lang==='de'?'nicht bezahlt':'not paid')} ({adminSelectedUser.subscription_status||'-'})</div>)}
          <div className="sec-lbl">{lang==='de'?'Dokumente':'Documents'}</div>
          {(adminSelectedUser.uploaded_docs||[]).length===0?(
            <div style={{fontSize:12,color:T.soft,marginBottom:14}}>{lang==='de'?'Keine Dokumente hochgeladen.':'No documents uploaded.'}</div>
          ):(
            (adminSelectedUser.uploaded_docs||[]).map((docLabel,idx)=>(
              <div key={idx} style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#181d25',border:'1px solid #232b36',borderRadius:10,padding:'10px 13px',marginBottom:8}}>
                <span style={{fontSize:13}}>{docLabel}</span>
                {adminUserDocUrls[docLabel]?(
                  <a href={adminUserDocUrls[docLabel]} target="_blank" rel="noopener noreferrer" style={{fontSize:11,fontWeight:700,color:'#e8f01a'}}>{lang==='de'?'Ansehen':'View'} ↗</a>
                ):(
                  <span style={{fontSize:11,color:T.soft}}>{lang==='de'?'Lädt...':'Loading...'}</span>
                )}
              </div>
            ))
          )}
          <button className="btn bs" onClick={()=>approveUser(adminSelectedUser)}>✓ {lang==='de'?'Freigeben':'Approve'}</button>
          <button className="btn bdan" onClick={()=>rejectUser(adminSelectedUser)}>✕ {lang==='de'?'Ablehnen':'Reject'}</button>
          <button className="btn bo" onClick={()=>setAdminSelectedUser(null)}>{t(lang,'close')}</button>
        </Overlay>
      )}
    </>
  );

  return(
    <>
      <style>{CSS}</style>
      <div className="app">
        <div className="layout">
          <LiveMap lang={lang}/>
          <div className="listcol">
            <div className="hdr">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div className="logo">EU<span>.</span>LOAD</div>
                <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',justifyContent:'flex-end'}}>
                 <button onClick={()=>setLang(lang==='de'?'en':'de')} title="Sprache / Language" style={{padding:'6px 11px',background:'rgba(136,146,164,0.1)',color:'#cbd5e0',border:'1px solid rgba(136,146,164,0.4)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer',marginRight:8,fontFamily:'Barlow Condensed',letterSpacing:0.5}}>🌐 {lang.toUpperCase()}</button>
                 <DemoTrackBtn/>
<TrackBtn/>
<NewLoadBtn/>
<StatusBtn/>
<RegisterBtn/>
<SignInBtn/>
<ShipperLoadsBtn/>
<MyLoadsBtn/>
<ProfileBtn/>
<LogoutBtn/>
<Pill/>
                </div>
              </div>
              <div style={{fontSize:9,color:T.soft,marginTop:3,letterSpacing:1.5,textTransform:'uppercase'}}>{t(lang,'tagline')}</div>
            </div>
            <div className="ll">
              {allLoads.map(load=>{
                const b=BADGE[load.type]||BADGE['Curtainside'];
                return(
                  <div key={load.id} className={'lc'+(load.isMine?' mine':'')} onClick={()=>onLoad(load)}>
                    {(load.status==='pending'||load.status==='assigned')&&<div className="res-ribbon">{t(lang,'reserved')}</div>}
                    {load.status==='awaiting_payer_payment'&&<div style={{position:'absolute',top:12,left:12,background:'linear-gradient(135deg,#f59e0b,#b45309)',color:'#fff',fontSize:9,fontWeight:800,letterSpacing:0.5,padding:'4px 9px',borderRadius:6,boxShadow:'0 2px 8px rgba(0,0,0,0.4)',zIndex:3}}>{lang==='de'?'Zahlung ausstehend':'Payment pending'}</div>}
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
                      <span className="tbg" style={{background:b.bg,color:b.color,borderColor:b.border}}>{load.type}</span>
                      <span className="price">{load.price.toLocaleString()} EUR</span>
                    </div>
                    <div className="route"><span>{load.from}</span><span className="arr">→</span><span>{load.to}</span></div>
                    <div className="meta">{load.shipper} · {load.weight} kg · {load.distance} km</div>
                    <div className="times">
                      <div className="time-item"><span className="time-lbl">{t(lang,'departure')}</span><span className="time-val">{load.depart}</span></div>
                      <div style={{color:'#232b36'}}>|</div>
                      <div className="time-item"><span className="time-lbl">{t(lang,'estArrival')}</span><span className="time-val">{load.arrive}</span></div>
                    </div>
                    <div style={{marginTop:8,paddingTop:8,borderTop:'1px solid #232b36',display:'flex',gap:5,flexWrap:'wrap',alignItems:'center'}}>
                      <span className="vt">{t(lang,'verified')}</span>
                      {load.insured?<span className="it">{t(lang,'insured')}</span>:<span className="at">{t(lang,'insurable')}</span>}
                      {load.isMine&&<span className="mine-tag">{t(lang,'myLoad')}</span>}
                      

                      
                      {load.loadRef&&<span className="ref-tag">{load.loadRef}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <SignInModal
        show={showLogin}
        loginPhone={loginPhone}
        setLoginPhone={setLoginPhone}
        loginPassword={loginPassword}
        setLoginPassword={setLoginPassword}
        lang={lang}
        onSignIn={async()=>{
          const isEmail=loginPhone.includes('@');
          const internalEmail=isEmail?loginPhone.trim():loginPhone.replace(/[^0-9]/g,'')+'@euload-driver.com';
          const result=await authSignIn(internalEmail,loginPassword);
          if(result){setShowLogin(false);await loadUserRole(result.id);setLoginPhone('');setLoginPassword('');}
        }}
        onClose={()=>{setShowLogin(false);setLoginPhone('');setLoginPassword('');}}
      />
      {showLocDisclosure&&(<Overlay onClose={()=>answerLocationConsent(false)}><div className={'ttl'}>{'\uD83D\uDCCD '}{lang==='de'?'Standortfreigabe':'Location access'}</div><div className={'sub'} style={{whiteSpace:'pre-line'}}>{lang==='de'?'EU.LOAD verwendet Ihren Standort f\u00fcr zwei Zwecke:\n\n1. Beim Anfragen einer Ladung wird gepr\u00fcft, ob Sie h\u00f6chstens 150 km von der Abholadresse entfernt sind.\n\n2. W\u00e4hrend eines Transports wird Ihre Position live an den Versender \u00fcbermittelt \u2013 auch wenn die App geschlossen oder der Bildschirm gesperrt ist. Eine dauerhafte Benachrichtigung zeigt an, dass die Ortung aktiv ist.\n\nNach der Zustellung wird die Ortung automatisch beendet. Ihre Standortdaten werden nur f\u00fcr diese Zwecke verwendet.\n\nBitte w\u00e4hlen Sie im n\u00e4chsten Schritt \u201eBei Nutzung der App\u201c.':'EU.LOAD uses your location for two purposes:\n\n1. When you request a load, we check that you are within 150 km of the pickup address.\n\n2. During a transport, your position is shared live with the shipper \u2013 even when the app is closed or the screen is locked. A persistent notification shows that tracking is active.\n\nTracking stops automatically after delivery. Your location data is used only for these purposes.\n\nIn the next step, please choose \u201cWhile using the app\u201d.'}</div><button className={'btn ba'} onClick={()=>answerLocationConsent(true)}>{lang==='de'?'Zustimmen':'Agree'}</button><button className={'btn bo'} onClick={()=>answerLocationConsent(false)}>{lang==='de'?'Ablehnen':'Decline'}</button></Overlay>)}
      {renderModal()}
    </>
  );
}









